import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import './App.css';
import AIAssistant from './components/AIAssistant';
import BackendWorkflowPanel from './components/BackendWorkflowPanel';
import DramaCanvas from './components/DramaCanvas';
import ElementInspectorFields, { InspectorEnums } from './components/ElementInspectorFields';
import LaneView from './components/LaneView';
import LandingHero from './components/LandingHero';
import ReferenceStrategyPanel, {
  NoveltyLevel,
  ReferenceMode,
} from './components/ReferenceStrategyPanel';
import StudioBoard from './components/StudioBoard';
import StudioSidebar, { StudioViewMode } from './components/StudioSidebar';
import ProjectHub from './components/ProjectHub';
import ExperiencePage from './components/ExperiencePage';
import ModelKeyControl from './components/ModelKeyControl';
import { EntryActionId, isAdaptationEntry } from './entryModel';
import { StudioIcon, StudioIconName } from './components/StudioIcons';
import WorkspaceStarterModal, { PresetFacets, PresetSeed } from './components/WorkspaceStarterModal';
import NovelAdaptationModal from './components/NovelAdaptationModal';
import XiaohongshuResearchPanel from './components/XiaohongshuResearchPanel';
import CriticNarrativePanel from './components/CriticNarrativePanel';
import CausalGraphPanel from './components/CausalGraphPanel';
import { analyzeWorkspaceImpact } from './impactAnalysis';
import { buildWorkbenchSteps, WorkbenchMode } from './workbenchModel';
import { resolveCollisions } from './canvasViewport';
import { guestLoginPath, requiresInviteCode } from './authPolicy';
import { API_BASE } from './apiBase';
import {
  APP_BRAND_NAME,
  APP_TEXT,
  Locale,
  META_BY_LOCALE,
  persistLocale,
  resolveInitialLocale,
} from './studioLocale';
import {
  firstEnumValue,
  normalizeEnumValue,
  toneChoiceIdToCanonical,
} from './utils/enumTranslator';

const ORDER = [
  'world_state',
  'theme',
  'character',
  'arc_milestone',
  'scene',
  'event',
  'plot_device',
  'dialogue',
];

type ViewMode = StudioViewMode;
type CanvasPosition = { x: number; y: number };
type DramaSeriesMode = 'short' | 'long';

const CANVAS_LAYOUT_STORAGE_PREFIX = 'studio.canvas.layout.v1';
const STARTUP_REQUEST_TIMEOUT_MS = 20000;

const VIEW_MODE_META: Record<
  Locale,
  Record<ViewMode, { label: string; hint: string; summary: string; icon: StudioIconName }>
> = {
  zh: {
    board: {
      label: '故事板',
      hint: '分区卡片',
      summary: '按戏剧要素分区浏览全部内容，适合整体梳理和快速筛选。',
      icon: 'board',
    },
    canvas: {
      label: '关系画布',
      hint: '拖动连线',
      summary: '自由摆放元素位置、创建连线，并在同一平面上整理故事关系。',
      icon: 'canvas',
    },
    lanes: {
      label: '结构泳道',
      hint: '只读结构',
      summary: '按幕、场、节拍查看结构推进；用于浏览和定位，编辑请切到画布。',
      icon: 'lanes',
    },
  },
  en: {
    board: {
      label: 'Board',
      hint: 'Sectioned cards',
      summary: 'Review every dramatic element by type for fast browsing and filtering.',
      icon: 'board',
    },
    canvas: {
      label: 'Canvas',
      hint: 'Drag and connect',
      summary: 'Arrange positions, draw links, and shape story relationships on one surface.',
      icon: 'canvas',
    },
    lanes: {
      label: 'Lanes',
      hint: 'Read-only structure',
      summary: 'Review the structure by act, scene, and beat; switch to Canvas for editing.',
      icon: 'lanes',
    },
  },
};

const CANVAS_X_BY_TYPE: Record<string, number> = {
  world_state: 64,
  theme: 64,
  character: 64,
  arc_milestone: 64,
  scene: 330,
  event: 330,
  plot_device: 760,
  dialogue: 760,
};

const CANVAS_Y_BY_TYPE: Record<string, number> = {
  world_state: 56,
  theme: 214,
  character: 372,
  arc_milestone: 548,
  scene: 86,
  event: 398,
  plot_device: 96,
  dialogue: 418,
};

const arr = (value: any) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
};

const firstEntry = (value: any) => {
  if (Array.isArray(value)) {
    return value[0] || null;
  }
  if (value && typeof value === 'object') {
    return Object.values(value)[0] || null;
  }
  return value || null;
};

const norm = (type: any) => {
  if (type === 'world') {
    return 'world_state';
  }
  if (type === 'device') {
    return 'plot_device';
  }
  return type || 'event';
};

const STANDARD_RELATIONSHIP_TO_FAMILY: Record<string, string> = {
  loves: 'emotional',
  hates: 'emotional',
  fears: 'emotional',
  trusts: 'emotional',
  betrays: 'emotional',
  competes_with: 'emotional',
  allies_with: 'emotional',
  mentors: 'emotional',
  influences: 'influence',
  empowers: 'influence',
  constrains: 'influence',
  reveals_about: 'influence',
  causes: 'causal',
  caused_by: 'causal',
  prevents: 'causal',
  enables: 'causal',
  follows: 'causal',
  parallels: 'causal',
  transforms: 'causal',
  participates_in: 'participation',
  observes: 'participation',
  contains: 'participation',
  setup_in: 'structural',
  payoff_in: 'structural',
  affects: 'structural',
  binds_to: 'structural',
};

const inferConnectionSemanticFamily = (value: any, fallback = 'influence') => {
  const label = String(value || '').trim();
  if (!label) {
    return fallback;
  }
  return STANDARD_RELATIONSHIP_TO_FAMILY[label] || 'custom';
};

const nameOf = (
  type: string,
  data: any,
  index: number,
  meta: Record<string, { label: string }>,
  unnamedLabel: string
) =>
  data?.name ||
  data?.title ||
  data?.scene_name ||
  data?.event_name ||
  data?.speaker ||
  `${meta[type]?.label || unnamedLabel} ${index + 1}`;

const parseLegacyCharacterName = (value: any) => {
  if (typeof value !== 'string') {
    return null;
  }
  const source = value.trim();
  if (!source.startsWith('{') || !source.endsWith('}')) {
    return null;
  }
  const nameMatch = source.match(/['"]name['"]\s*:\s*['"]([^'"]+)['"]/);
  const typeMatch = source.match(/['"]character_type['"]\s*:\s*['"]([^'"]+)['"]/);
  if (!nameMatch && !typeMatch) {
    return null;
  }
  return {
    name: nameMatch?.[1] || '',
    character_type: typeMatch?.[1] || '',
  };
};

const normalizeCharacterEntry = (entry: any) => {
  if (!entry || typeof entry !== 'object') {
    return entry;
  }
  const legacy = parseLegacyCharacterName(entry.name);
  if (!legacy) {
    return entry;
  }
  const next = { ...entry };
  if (legacy.name) {
    next.name = legacy.name;
    if (!next.title || parseLegacyCharacterName(next.title)) {
      next.title = legacy.name;
    }
  }
  if (legacy.character_type) {
    next.character_type = legacy.character_type;
  }
  return next;
};

const card = (
  type: any,
  raw: any,
  index: number,
  meta: Record<string, { label: string }>,
  unnamedLabel: string
) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const normalizedType = norm(type || raw.type);
  const entry = normalizedType === 'character' ? normalizeCharacterEntry(raw) : raw;
  const backendId =
    entry.id ||
    entry.scene_id ||
    entry.event_id ||
    entry.dialogue_id ||
    entry.character_id ||
    `${normalizedType}_${index}`;

  return {
    id: String(backendId),
    type: normalizedType,
    name: nameOf(normalizedType, entry, index, meta, unnamedLabel),
    data: {
      ...entry,
      backendId: String(backendId),
    },
  };
};

const fromPayload = (
  payload: any,
  meta: Record<string, { label: string }>,
  unnamedLabel: string
) => {
  const items: any[] = [];
  const synthesizedCharacters =
    Array.isArray(payload?.characters) && payload.characters.length
      ? payload.characters
      : arr(payload?.character_arcs).map((arc: any, index: number) => ({
          id: arc?.character_id || `character_arc_${index + 1}`,
          name: arc?.character_name || arc?.name || `角色 ${index + 1}`,
          character_type: arc?.character_type || (index === 0 ? 'protagonist' : 'helper'),
          background: arc?.description || '',
          goal: arc?.want || '',
          motivation: arc?.need || '',
          obstacle: arc?.lie || '',
          arc: arc?.truth || arc?.arc_description || '',
        }));
  const push = (type: string, list: any) => {
    arr(list).forEach((entry, index) => {
      const nextCard = card(type, entry, items.length + index, meta, unnamedLabel);
      if (nextCard) {
        items.push(nextCard);
      }
    });
  };

  const worldState =
    payload?.worldview ||
    payload?.world_state ||
    payload?.worldState ||
    firstEntry(payload?.worlds) ||
    firstEntry(payload?.world_states);

  if (worldState) {
    push('world_state', [worldState]);
  }
  push('theme', payload?.themes);
  push('character', synthesizedCharacters);
  push('arc_milestone', payload?.arc_milestones);

  const effectiveScenes = arr(payload?.scenes);
  push('scene', effectiveScenes);
  push('event', payload?.events);
  push('plot_device', payload?.plot_devices);
  push('dialogue', payload?.dialogues);

  const seenIds = new Set<string>();
  const elements = items.filter((item) => {
    if (seenIds.has(item.id)) {
      return false;
    }
    seenIds.add(item.id);
    return true;
  });

  const connections = arr(payload?.connections)
    .map((item: any, index: number) => {
      const relationshipType = String(item?.relationship_type || item?.relationshipType || 'influences');
      return {
        id: String(item?.id || `c_${index}`),
        sourceId: String(item?.source_id || item?.sourceId || ''),
        targetId: String(item?.target_id || item?.targetId || ''),
        relationshipType,
        semanticFamily: String(
          item?.semantic_family || item?.semanticFamily || inferConnectionSemanticFamily(relationshipType)
        ),
        description: String(item?.description || item?.ai_analysis || ''),
      };
    })
    .filter((item: any) => item.sourceId && item.targetId);

  return { elements, connections };
};

const DEFAULT_AUTH_STATE = {
  authenticated: false,
  user: null,
  providers: {
    wechat: false,
    invite_code: false,
    owner: false,
    guest: false,
  },
  inviteCodeRequired: false,
  inviteCodeLabel: '',
  recentRuns: [],
  workspaceIntegrity: {
    valid: true,
    issues: [],
  },
};

const filenameFromDisposition = (value?: string) => {
  if (!value) {
    return '';
  }

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch (_error) {
      return utf8Match[1];
    }
  }

  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || '';
};

const safeDownloadName = (value: string, fallback: string) => {
  const next = value.replace(/[<>:"/\\|?*\s]+/g, '_').trim();
  return (next || fallback).slice(0, 80);
};

const normalizeCanvasLayout = (value: any): Record<string, CanvasPosition> => {
  try {
    if (!value || typeof value !== 'object') {
      return {};
    }

    return Object.entries(value).reduce<Record<string, CanvasPosition>>((acc, [id, position]) => {
      const next = position as CanvasPosition | undefined;
      if (
        next &&
        typeof next.x === 'number' &&
        Number.isFinite(next.x) &&
        typeof next.y === 'number' &&
        Number.isFinite(next.y)
      ) {
        acc[id] = {
          x: Math.max(0, next.x),
          y: Math.max(0, next.y),
        };
      }
      return acc;
    }, {});
  } catch (_error) {
    return {};
  }
};

const readCanvasLayout = (storageKey: string): Record<string, CanvasPosition> => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {};
    }

    return normalizeCanvasLayout(JSON.parse(raw));
  } catch (_error) {
    return {};
  }
};

const writeCanvasLayout = (storageKey: string, layout: Record<string, CanvasPosition>) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(layout));
  } catch (_error) {
    // Ignore storage failures and keep the in-memory layout working.
  }
};

const defaultCanvasPosition = (item: any, slot: number): CanvasPosition => {
  const type = norm(item.type);
  const baseX = CANVAS_X_BY_TYPE[type] ?? 80;
  const baseY = CANVAS_Y_BY_TYPE[type] ?? 80;

  return {
    x: baseX,
    y: baseY + slot * 138,
  };
};

const normalizeCanvasEpisode = (value: any) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  const match = raw.match(/(\d+)/);
  if (match) {
    return `第${Number(match[1])}集`;
  }
  return raw;
};

const canvasEpisodeSortValue = (value: string) => {
  const match = String(value || '').match(/(\d+)/);
  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Number(match[1]);
};

// Canvas lanes are a view concern, not a dramaturgy constraint.  Prefer the
// structure the project actually contains (episodes, chapters, sequences or
// named phases); a bare collection of scenes stays a single chronological lane.
const canvasStructureLabel = (data: any) => {
  const value = data?.story_phase ?? data?.phase ?? data?.chapter ?? data?.sequence ?? data?.act ?? data?.act_type ?? data?.actType;
  return String(value || '').trim();
};

const canvasStructureSortValue = (value: string) => {
  const match = String(value || '').match(/(\d+)/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

const buildCanvasLanePlan = (elements: any[]) => {
  const sceneStructureById = new Map<string, string>();
  const sceneEpisodeById = new Map<string, string>();

  elements.forEach((item) => {
    if (norm(item.type) !== 'scene') {
      return;
    }
    const id = String(item.id);
    const structure = canvasStructureLabel(item.data);
    if (structure) sceneStructureById.set(id, structure);
    const episode = normalizeCanvasEpisode(item.data?.episode);
    if (episode) {
      sceneEpisodeById.set(id, episode);
    }
  });

  const eventSceneIdById = new Map<string, string>();
  elements.forEach((item) => {
    if (norm(item.type) !== 'event') {
      return;
    }
    eventSceneIdById.set(String(item.id), String(item.data?.scene_id || item.data?.sceneId || ''));
  });

  const activeEpisodes = Array.from(new Set(sceneEpisodeById.values())).sort((left, right) => {
    return canvasEpisodeSortValue(left) - canvasEpisodeSortValue(right) || left.localeCompare(right);
  });

  const activeStructures = Array.from(new Set(sceneStructureById.values())).sort((left, right) => {
    return canvasStructureSortValue(left) - canvasStructureSortValue(right) || left.localeCompare(right);
  });
  const mode: 'episode' | 'structure' | 'sequence' = activeEpisodes.length > 1 ? 'episode' : activeStructures.length > 1 ? 'structure' : 'sequence';
  const lanes = mode === 'episode' ? activeEpisodes : mode === 'structure' ? activeStructures : ['场景序列'];

  const inferLaneForItem = (item: any) => {
    const type = norm(item.type);
    if (mode === 'episode') {
      if (type === 'scene') {
        return sceneEpisodeById.get(String(item.id)) || '';
      }
      if (type === 'event') {
        const sceneId = eventSceneIdById.get(String(item.id)) || '';
        return sceneEpisodeById.get(sceneId) || '';
      }
      if (type === 'dialogue') {
        const sceneId = String(item.data?.scene_id || item.data?.sceneId || '');
        return sceneEpisodeById.get(sceneId) || '';
      }
      if (type === 'plot_device') {
        const linkedSceneId =
          String(
            item.data?.attached_scene_id ||
              item.data?.attachedSceneId ||
              item.data?.setup_nodes?.[0]?.scene_id ||
              item.data?.payoff_nodes?.[0]?.scene_id ||
              item.data?.links?.scenes?.[0] ||
              ''
          );
        if (linkedSceneId) {
          return sceneEpisodeById.get(linkedSceneId) || '';
        }
        const linkedEventId = String(item.data?.attached_event_id || item.data?.attachedEventId || '');
        if (linkedEventId) {
          const sceneId = eventSceneIdById.get(linkedEventId) || '';
          return sceneEpisodeById.get(sceneId) || '';
        }
      }
      return '';
    }

    if (type === 'scene') {
      return sceneStructureById.get(String(item.id)) || lanes[0];
    }
    if (type === 'event') {
      const sceneId = eventSceneIdById.get(String(item.id)) || '';
      return sceneStructureById.get(sceneId) || canvasStructureLabel(item.data) || lanes[0];
    }
    if (type === 'dialogue') {
      const sceneId = String(item.data?.scene_id || item.data?.sceneId || '');
      return sceneStructureById.get(sceneId) || lanes[0];
    }
    if (type === 'plot_device') {
      const linkedSceneId =
        item.data?.attached_scene_id ||
        item.data?.attachedSceneId ||
        item.data?.setup_nodes?.[0]?.scene_id ||
        item.data?.payoff_nodes?.[0]?.scene_id ||
        item.data?.links?.scenes?.[0] ||
        '';
      if (linkedSceneId) {
        return sceneStructureById.get(String(linkedSceneId)) || lanes[0];
      }
      const linkedEventId = item.data?.attached_event_id || item.data?.attachedEventId || '';
      if (linkedEventId) {
        const sceneId = eventSceneIdById.get(String(linkedEventId)) || '';
        return sceneStructureById.get(sceneId) || lanes[0];
      }
    }
    return lanes[0];
  };

  return {
    mode,
    lanes,
    inferLaneForItem,
  };
};

const buildAutoCanvasLayout = (
  elements: any[],
  currentLayout: Record<string, CanvasPosition>,
  options?: { overwrite?: boolean }
) => {
  const overwrite = Boolean(options?.overwrite);
  const nextLayout: Record<string, CanvasPosition> = overwrite ? {} : { ...currentLayout };
  const lanePlan = buildCanvasLanePlan(elements);

  const actItems = elements.filter((item) =>
    ['scene', 'event', 'plot_device', 'dialogue'].includes(norm(item.type))
  );
  const laneKeys = lanePlan.lanes.filter((lane) => actItems.some((item) => lanePlan.inferLaneForItem(item) === lane));
  const activeLanes = laneKeys.length > 0 ? laneKeys : lanePlan.lanes;
  const laneWidth = Math.max(280, Math.round(1480 / Math.max(1, activeLanes.length)));
  const laneX = new Map<string, number>(activeLanes.map((lane, index) => [lane, index * laneWidth]));
  const sceneOffset = Math.round(Math.min(182, laneWidth * 0.34));
  const eventOffset = Math.round(Math.min(334, laneWidth * 0.64));

  const setPosition = (elementId: string, x: number, y: number) => {
    if (!overwrite && nextLayout[elementId]) {
      return;
    }
    nextLayout[elementId] = {
      x: Math.max(16, Math.round(x)),
      y: Math.max(16, Math.round(y)),
    };
  };

  elements
    .filter((item) => norm(item.type) === 'world_state')
    .forEach((item, index) => setPosition(String(item.id), 36, 48 + index * 148));

  elements
    .filter((item) => norm(item.type) === 'theme')
    .forEach((item, index) => setPosition(String(item.id), 36, 206 + index * 138));

  elements
    .filter((item) => norm(item.type) === 'character')
    .forEach((item, index) => setPosition(String(item.id), 320, 88 + index * 158));

  elements
    .filter((item) => norm(item.type) === 'arc_milestone')
    .forEach((item, index) => setPosition(String(item.id), 36, 618 + index * 126));

  const scenesByAct = new Map<string, any[]>();
  activeLanes.forEach((lane) => scenesByAct.set(lane, []));
  elements
    .filter((item) => norm(item.type) === 'scene')
    .forEach((item) => {
      const lane = lanePlan.inferLaneForItem(item) || activeLanes[0];
      scenesByAct.set(lane, [...(scenesByAct.get(lane) || []), item]);
    });

  const sceneYById = new Map<string, number>();
  activeLanes.forEach((lane) => {
    (scenesByAct.get(lane) || []).forEach((item, index) => {
      const x = (laneX.get(lane) || 0) + sceneOffset;
      const y = 88 + index * 244;
      setPosition(String(item.id), x, y);
      sceneYById.set(String(item.id), y);
    });
  });

  const looseEventSlots = new Map<string, number>(activeLanes.map((lane) => [lane, 0]));
  const eventSlotsByScene = new Map<string, number>();
  elements
    .filter((item) => norm(item.type) === 'event')
    .forEach((item) => {
      const act = lanePlan.inferLaneForItem(item) || activeLanes[0];
      const sceneId = String(item.data?.scene_id || item.data?.sceneId || '');
      const x = (laneX.get(act) || 0) + eventOffset;
      if (sceneId && sceneYById.has(sceneId)) {
        const slot = eventSlotsByScene.get(sceneId) || 0;
        setPosition(String(item.id), x, (sceneYById.get(sceneId) || 88) + 118 + slot * 92);
        eventSlotsByScene.set(sceneId, slot + 1);
        return;
      }
      const slot = looseEventSlots.get(act) || 0;
      setPosition(String(item.id), x, 118 + slot * 178);
      looseEventSlots.set(act, slot + 1);
    });

  const deviceSlotsByAct = new Map<string, number>(activeLanes.map((lane) => [lane, 0]));
  elements
    .filter((item) => norm(item.type) === 'plot_device')
    .forEach((item) => {
      const act = lanePlan.inferLaneForItem(item) || activeLanes[0];
      const slot = deviceSlotsByAct.get(act) || 0;
      const sceneCount = (scenesByAct.get(act) || []).length;
      setPosition(String(item.id), (laneX.get(act) || 0) + sceneOffset, 128 + sceneCount * 244 + slot * 148);
      deviceSlotsByAct.set(act, slot + 1);
    });

  const dialogueSlotsByScene = new Map<string, number>();
  const looseDialogueSlots = new Map<string, number>(activeLanes.map((lane) => [lane, 0]));
  elements
    .filter((item) => norm(item.type) === 'dialogue')
    .forEach((item) => {
      const act = lanePlan.inferLaneForItem(item) || activeLanes[0];
      const sceneId = String(item.data?.scene_id || item.data?.sceneId || '');
      const x = (laneX.get(act) || 0) + eventOffset;
      if (sceneId && sceneYById.has(sceneId)) {
        const slot = dialogueSlotsByScene.get(sceneId) || 0;
        setPosition(String(item.id), x, (sceneYById.get(sceneId) || 88) + 28 + slot * 82);
        dialogueSlotsByScene.set(sceneId, slot + 1);
        return;
      }
      const slot = looseDialogueSlots.get(act) || 0;
      setPosition(String(item.id), x, 84 + slot * 156);
      looseDialogueSlots.set(act, slot + 1);
    });

  const collisionFree = resolveCollisions(
    elements.map((item) => {
      const position = nextLayout[String(item.id)] || defaultCanvasPosition(item, 0);
      const dataLength = JSON.stringify(item.data || {}).length;
      return {
        id: String(item.id),
        x: position.x,
        y: position.y,
        width: 276,
        height: Math.min(320, 150 + Math.floor(dataLength / 180) * 34),
      };
    }),
    { horizontal: 32, vertical: 28 }
  );
  return collisionFree.reduce<Record<string, CanvasPosition>>((layout, node) => {
    layout[node.id] = { x: node.x, y: node.y };
    return layout;
  }, {});
};

const shouldReflowCanvasLayout = (elements: any[], layout: Record<string, CanvasPosition>) => {
  if (!layout || Object.keys(layout).length === 0) {
    return true;
  }

  const staged = elements.filter((item) =>
    ['scene', 'event', 'plot_device', 'dialogue'].includes(norm(item.type))
  );
  if (staged.length === 0) {
    return false;
  }

  const positions = staged
    .map((item) => layout[item.id])
    .filter((position): position is CanvasPosition => Boolean(position));
  if (positions.length < Math.max(2, Math.floor(staged.length * 0.6))) {
    return true;
  }

  const xs = positions.map((position) => position.x);
  const spread = Math.max(...xs) - Math.min(...xs);
  if (staged.length >= 4 && spread < 260) {
    return true;
  }

  // FIX: also detect stacking of non-staged nodes (themes, characters, milestones)
  const allPositions = elements
    .map((item) => layout[item.id])
    .filter((position): position is CanvasPosition => Boolean(position));
  if (allPositions.length >= 4) {
    const allXs = allPositions.map((p) => p.x);
    const allSpread = Math.max(...allXs) - Math.min(...allXs);
    if (allSpread < 260) {
      return true;
    }
  }

  const scenes = elements.filter((item) => norm(item.type) === 'scene');
  const lanePlan = buildCanvasLanePlan(elements);
  const sceneActs = new Set(scenes.map((item) => lanePlan.inferLaneForItem(item)));
  const sceneColumns = new Set(
    scenes
      .map((item) => layout[item.id])
      .filter((position): position is CanvasPosition => Boolean(position))
      .map((position) => Math.round(position.x / 220))
  );
  if (sceneActs.size > 1 && sceneColumns.size < Math.min(sceneActs.size, 2)) {
    return true;
  }

  return false;
};

const createLocalId = (type: string) =>
  `${norm(type)}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

type LocalElementCardOptions = {
  meta: Record<string, { label: string }>;
  unnamedLabel: string;
  createItemName: (label: string) => string;
  enumOptions?: InspectorEnums;
  deviceTypes?: Array<{ type: string }>;
};

const buildLocalElementCard = (
  type: string,
  draft: Record<string, any> = {},
  { meta, unnamedLabel, createItemName, enumOptions, deviceTypes }: LocalElementCardOptions
) => {
  const normalizedType = norm(type);
  const label = meta[normalizedType]?.label || meta[type]?.label || unnamedLabel;
  const itemName = createItemName(label);
  const id = String(draft.id || draft.backendId || createLocalId(normalizedType));
  const raw: Record<string, any> = {
    id,
    name: itemName,
    title: itemName,
  };

  if (normalizedType === 'world_state') {
    raw.genre = firstEnumValue(enumOptions?.GenreType, 'tragedy');
    raw.rules = [];
  } else if (normalizedType === 'character') {
    raw.character_type = firstEnumValue(enumOptions?.CharacterType, 'protagonist');
    raw.dialogue_style = '';
    raw.writing_style = firstEnumValue(enumOptions?.WritingStyle, '');
    raw.writing_tone = firstEnumValue(enumOptions?.WritingTone, '');
    raw.personality_traits = [];
  } else if (normalizedType === 'scene') {
    raw.act = firstEnumValue(enumOptions?.ActType, 'act_one');
    raw.episode = '';
    raw.characters_present = [];
    raw.events = [];
    raw.conflict_axes = [];
    raw.tension_level = 1;
  } else if (normalizedType === 'event') {
    raw.act = firstEnumValue(enumOptions?.ActType, 'act_one');
    raw.beat = firstEnumValue(enumOptions?.BeatType, '');
    raw.conflict_type = firstEnumValue(enumOptions?.ConflictType, 'man_vs_man');
    raw.emotional_impact = firstEnumValue(enumOptions?.EmotionType, 'surprise');
    raw.scene_id = '';
  } else if (normalizedType === 'plot_device') {
    raw.device_type = firstEnumValue(deviceTypes?.map((item) => item.type), 'Foreshadowing');
    raw.description = '';
    raw.links = {
      scenes: [],
      characters: [],
      relationships: [],
      beats: [],
      world_rules: [],
      props: [],
    };
    raw.setup_nodes = [];
    raw.payoff_nodes = [];
  } else if (normalizedType === 'dialogue') {
    raw.speaker = itemName;
    raw.content = '';
    raw.text = '';
    raw.emotion = firstEnumValue(enumOptions?.EmotionType, 'joy');
  } else if (normalizedType === 'arc_milestone') {
    raw.character_id = '';
    raw.milestone_type = firstEnumValue(enumOptions?.BeatType, 'Need');
    raw.note = '';
  }

  const nextData: Record<string, any> = {
    ...raw,
    ...draft,
    id,
    backendId: id,
  };
  const nextName =
    nextData.name ||
    nextData.title ||
    nextData.scene_name ||
    nextData.event_name ||
    nextData.speaker ||
    itemName;

  return {
    label,
    item: {
      id,
      type: normalizedType,
      name: String(nextName || itemName),
      data: nextData,
    },
  };
};

const PRESET_SEED_COPY = {
  zh: {
    worldName: '预设世界观',
    deviceName: '核心戏剧装置',
    characterNames: ['主角', '对位角色', '关键第三人'],
    supportCharacterNames: ['盟友', '对手', '家人', '职场线角色', '旧关系角色', '功能角色'],
    sceneTemplates: [
      {
        title: '开场局面',
        summary: '建立当前秩序、关系张力和人物欲望。',
        act: 'act_one',
        beat: 'OPENING_IMAGE',
      },
      {
        title: '第一次加压',
        summary: '让冲突第一次显形，并迫使人物作出选择。',
        act: 'act_one',
        beat: 'CATALYST',
      },
      {
        title: '中段反转',
        summary: '把核心关系或信息差推到新的层级。',
        act: 'act_two',
        beat: 'MIDPOINT',
      },
      {
        title: '终局回收',
        summary: '让铺垫兑现，并把代价与情绪结清。',
        act: 'act_three',
        beat: 'FINALE',
      },
    ],
    extendedSceneTemplates: [
      {
        title: '开场局面',
        summary: '建立当前秩序、人物位置和核心欲望。',
        act: 'act_one',
        beat: 'OPENING_IMAGE',
      },
      {
        title: '诱发事件',
        summary: '让人物无法继续维持表面平衡。',
        act: 'act_one',
        beat: 'CATALYST',
      },
      {
        title: '局势升级',
        summary: '让关系和利益拉扯进入公开博弈。',
        act: 'act_two',
        beat: 'FUN_AND_GAMES',
      },
      {
        title: '中段反转',
        summary: '抬高信息差、身份差或代价密度。',
        act: 'act_two',
        beat: 'MIDPOINT',
      },
      {
        title: '代价逼近',
        summary: '让角色付出代价，并暴露新的脆弱点。',
        act: 'act_two',
        beat: 'ALL_IS_LOST',
      },
      {
        title: '终局回收',
        summary: '把前面的铺垫和情绪线统一结算。',
        act: 'act_three',
        beat: 'FINALE',
      },
    ],
  },
  en: {
    worldName: 'Preset world',
    deviceName: 'Core dramatic device',
    characterNames: ['Protagonist', 'Counterpart', 'Third pivot'],
    supportCharacterNames: ['Ally', 'Rival', 'Family Pivot', 'Workline Character', 'Legacy Connection', 'Utility Character'],
    sceneTemplates: [
      {
        title: 'Opening pressure',
        summary: 'Establish the social order, relational tension, and immediate desire.',
        act: 'act_one',
        beat: 'OPENING_IMAGE',
      },
      {
        title: 'First escalation',
        summary: 'Let conflict surface and force the first meaningful choice.',
        act: 'act_one',
        beat: 'CATALYST',
      },
      {
        title: 'Midpoint reversal',
        summary: 'Shift the central relationship, information gap, or status line.',
        act: 'act_two',
        beat: 'MIDPOINT',
      },
      {
        title: 'Final payoff',
        summary: 'Cash in the setup and settle the emotional cost.',
        act: 'act_three',
        beat: 'FINALE',
      },
    ],
    extendedSceneTemplates: [
      {
        title: 'Opening pressure',
        summary: 'Establish order, desire, and the initial fault line.',
        act: 'act_one',
        beat: 'OPENING_IMAGE',
      },
      {
        title: 'Catalyst',
        summary: 'Break the surface balance so the story has to move.',
        act: 'act_one',
        beat: 'CATALYST',
      },
      {
        title: 'Escalation lane',
        summary: 'Push the relationship and status game into open action.',
        act: 'act_two',
        beat: 'FUN_AND_GAMES',
      },
      {
        title: 'Midpoint reversal',
        summary: 'Increase the information gap, identity risk, or visible cost.',
        act: 'act_two',
        beat: 'MIDPOINT',
      },
      {
        title: 'Approaching loss',
        summary: 'Make the price tangible and expose a fresh vulnerability.',
        act: 'act_two',
        beat: 'ALL_IS_LOST',
      },
      {
        title: 'Final payoff',
        summary: 'Resolve the setup chain and close the emotional line.',
        act: 'act_three',
        beat: 'FINALE',
      },
    ],
  },
} as const;

const resolvePresetCharacterCount = (targetEpisodeCount: number) => {
  if (targetEpisodeCount >= 120) return 10;
  if (targetEpisodeCount >= 60) return 8;
  if (targetEpisodeCount >= 30) return 7;
  if (targetEpisodeCount >= 12) return 6;
  return 5;
};

const selectCharacterType = (index: number, allowedTypes: string[]) => {
  if (!allowedTypes.length) return '';
  const priorities = ['protagonist', 'deuteragonist', 'antagonist', 'foil', 'mentor', 'helper'];
  const desired = priorities[index] || 'helper';
  return normalizeEnumValue(desired, allowedTypes, 'CharacterType') || allowedTypes[Math.min(index, allowedTypes.length - 1)];
};

const normalizeBeatKey = (beat: string) => String(beat || '').trim().toLowerCase();

const inferSceneConflictAxes = (beat: string, conflictEnums: string[]) => {
  const key = normalizeBeatKey(beat);
  const prefer =
    key === 'all_is_lost' || key === 'dark_night_of_soul'
      ? ['man_vs_self', 'man_vs_fate']
      : key === 'midpoint' || key === 'break_into_three'
      ? ['man_vs_man', 'man_vs_society']
      : key === 'catalyst' || key === 'debate'
      ? ['man_vs_society', 'man_vs_self']
      : ['man_vs_man'];
  return prefer
    .map((item) => normalizeEnumValue(item, conflictEnums))
    .filter(Boolean)
    .slice(0, 2);
};

const inferArcStage = (beat: string) => {
  const key = normalizeBeatKey(beat);
  if (['opening_image', 'theme_stated', 'setup'].includes(key)) return 'setup';
  if (['catalyst', 'debate', 'break_into_two'].includes(key)) return 'pressure';
  if (['fun_and_games', 'midpoint', 'bad_guys_close_in'].includes(key)) return 'break';
  if (['all_is_lost', 'dark_night_of_soul', 'break_into_three'].includes(key)) return 'shift';
  return 'payoff';
};

const buildCharacterProfiles = (roster: string[], conflictEnums: string[]) =>
  roster.map((name, index) => {
    const baseFocus =
      index === 0
        ? ['man_vs_self', 'man_vs_society']
        : index === 1
        ? ['man_vs_man', 'man_vs_society']
        : index === 2
        ? ['man_vs_man', 'man_vs_fate']
        : index % 2 === 0
        ? ['man_vs_society', 'man_vs_technology']
        : ['man_vs_self', 'man_vs_man'];
    return {
      name,
      roleWeight: index === 0 ? 4 : index === 1 ? 3 : index === 2 ? 3 : 1,
      arcPhaseBias: index <= 1 ? ['setup', 'pressure', 'break', 'shift', 'payoff'] : ['pressure', 'break', 'shift'],
      conflictFocus: baseFocus
        .map((item) => normalizeEnumValue(item, conflictEnums))
        .filter(Boolean),
    };
  });

const buildSceneCharacterCast = (
  beat: string,
  sceneConflictAxes: string[],
  profiles: Array<{ name: string; roleWeight: number; arcPhaseBias: string[]; conflictFocus: string[] }>,
  sceneIndex: number
) => {
  if (!profiles.length) return [];
  const stage = inferArcStage(beat);
  const scored = profiles
    .map((profile) => {
      const conflictHit = profile.conflictFocus.some((axis) => sceneConflictAxes.includes(axis)) ? 2 : 0;
      const stageHit = profile.arcPhaseBias.includes(stage) ? 1 : 0;
      const rotationBias = sceneIndex % Math.max(2, profiles.length) === profiles.indexOf(profile) ? 1 : 0;
      return { ...profile, score: profile.roleWeight + conflictHit + stageHit + rotationBias };
    })
    .sort((left, right) => right.score - left.score);
  const denseBeats = new Set(['catalyst', 'midpoint', 'all_is_lost', 'finale', 'break_into_three']);
  const desiredSize = denseBeats.has(normalizeBeatKey(beat)) ? 4 : 3;
  return scored.slice(0, Math.min(desiredSize, scored.length)).map((item) => item.name);
};

const buildPresetSeedElements = ({
  seed,
  locale,
  meta,
  unnamedLabel,
  createItemName,
  enumOptions,
  deviceTypes,
}: {
  seed: PresetSeed;
  locale: Locale;
  meta: Record<string, { label: string }>;
  unnamedLabel: string;
  createItemName: (label: string) => string;
  enumOptions: InspectorEnums;
  deviceTypes: Array<{ type: string }>;
}) => {
  const copy = PRESET_SEED_COPY[locale];
  const options = { meta, unnamedLabel, createItemName, enumOptions, deviceTypes };
  const items: any[] = [];
  const worldviewText = String(seed.worldview || '').trim();
  const themeNames = Array.from(new Set(themeAtomsFromText(seed.themes))).slice(0, 4);
  const engineLabel = String(seed.presetFacets?.choice_labels?.engine || copy.deviceName).trim();
  const toneChoiceId = String(seed.presetFacets?.choice_ids?.tone || '').trim();
  const writingTone = toneChoiceIdToCanonical(toneChoiceId);
  const promptText = String(seed.prompt || '').trim();

  if (worldviewText) {
    items.push(
      buildLocalElementCard(
        'world_state',
        {
          name: copy.worldName,
          title: copy.worldName,
          premise: worldviewText,
          description: worldviewText,
          summary: worldviewText,
        },
        options
      ).item
    );
  }

  themeNames.forEach((themeName) => {
    items.push(
      buildLocalElementCard(
        'theme',
        {
          name: themeName,
          title: themeName,
          description: themeName,
        },
        options
      ).item
    );
  });

  const characterCount = resolvePresetCharacterCount(seed.targetEpisodeCount || 0);
  const characterRoster = [...copy.characterNames, ...copy.supportCharacterNames].slice(0, characterCount);
  const characterProfiles = buildCharacterProfiles(characterRoster, enumOptions.ConflictType || []);
  characterRoster.forEach((characterName, index) => {
    items.push(
      buildLocalElementCard(
        'character',
        {
          name: characterName,
          title: characterName,
          character_type: selectCharacterType(index, enumOptions.CharacterType) || 'protagonist',
          goal: engineLabel,
          motivation: worldviewText || promptText,
          background: worldviewText,
          arc_stage: index <= 1 ? 'setup' : 'pressure',
          conflict_focus: characterProfiles[index]?.conflictFocus || [],
          writing_tone: writingTone || '',
        },
        options
      ).item
    );
  });

  const sceneTemplates =
    (seed.targetEpisodeCount || 0) > 10 ? copy.extendedSceneTemplates : copy.sceneTemplates;
  sceneTemplates.forEach((sceneTemplate, index) => {
    const sceneConflictAxes = inferSceneConflictAxes(sceneTemplate.beat, enumOptions.ConflictType || []);
    items.push(
      buildLocalElementCard(
        'scene',
        {
          name: sceneTemplate.title,
          title: sceneTemplate.title,
          summary: sceneTemplate.summary,
          purpose: sceneTemplate.summary,
          act: sceneTemplate.act,
          beat: sceneTemplate.beat,
          episode: String(index + 1),
          conflict_axes: sceneConflictAxes,
          characters_present: buildSceneCharacterCast(
            sceneTemplate.beat,
            sceneConflictAxes,
            characterProfiles,
            index
          ),
        },
        options
      ).item
    );
  });

  if (engineLabel || promptText) {
    items.push(
      buildLocalElementCard(
        'plot_device',
        {
          name: engineLabel || copy.deviceName,
          title: engineLabel || copy.deviceName,
          description: [promptText, worldviewText].filter(Boolean).join(locale === 'zh' ? '；' : '; '),
          device_type: firstEnumValue(deviceTypes?.map((item) => item.type), 'Foreshadowing'),
        },
        options
      ).item
    );
  }

  return items;
};

const cloneForPayload = (item: any) => {
  const raw = { ...(item?.data || {}) };
  const id = String(raw.backendId || raw.id || item.id);
  delete raw.backendId;
  return { ...raw, id };
};

const buildWorkspacePayload = (
  elements: any[],
  connections: any[],
  worldviewText: string,
  themesText: string,
  canvasLayout: Record<string, CanvasPosition>,
  targetEpisodeCount: number,
  dramaSeriesMode: DramaSeriesMode,
  presetFacets: PresetFacets | null,
  enumOptions: InspectorEnums
) => {
  const payload: any = {
    worlds: [],
    themes: [],
    characters: [],
    arc_milestones: [],
    scenes: [],
    events: [],
    plot_devices: [],
    dialogues: [],
    connections: [],
    workspace_meta: {
      worldview_text: worldviewText,
      themes_text: themesText,
      canvas_layout: canvasLayout,
      target_episode_count: targetEpisodeCount > 0 ? targetEpisodeCount : undefined,
      drama_series_mode: dramaSeriesMode,
      routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
      preset_facets: presetFacets || undefined,
    },
  };

  elements.forEach((item) => {
    const type = norm(item.type);
    const raw = type === 'character' ? normalizeCharacterEntry(cloneForPayload(item)) : cloneForPayload(item);

    if (type === 'world_state') {
      payload.worlds.push({
        ...raw,
        genre: normalizeEnumValue(raw.genre, enumOptions.GenreType),
        name: raw.name || item.name || '世界观',
      });
      return;
    }

    if (type === 'theme') {
      payload.themes.push({
        ...raw,
        title: raw.title || raw.name || item.name,
        name: raw.name || raw.title || item.name,
      });
      return;
    }

    if (type === 'character') {
      payload.characters.push({
        ...raw,
        character_type: normalizeEnumValue(raw.character_type, enumOptions.CharacterType, 'CharacterType'),
        writing_style: normalizeEnumValue(raw.writing_style, enumOptions.WritingStyle),
        writing_tone: normalizeEnumValue(raw.writing_tone, enumOptions.WritingTone, 'WritingTone'),
        name: raw.name || item.name,
      });
      return;
    }

    if (type === 'arc_milestone') {
      payload.arc_milestones.push({
        ...raw,
        title: raw.title || item.name,
      });
      return;
    }

    if (type === 'scene') {
      payload.scenes.push({
        ...raw,
        act: normalizeEnumValue(raw.act, enumOptions.ActType, 'ActType'),
        title: raw.title || raw.name || item.name,
      });
      return;
    }

    if (type === 'event') {
      payload.events.push({
        ...raw,
        act: normalizeEnumValue(raw.act, enumOptions.ActType, 'ActType'),
        beat: normalizeEnumValue(raw.beat, enumOptions.BeatType),
        conflict_type: normalizeEnumValue(raw.conflict_type, enumOptions.ConflictType),
        emotional_impact: normalizeEnumValue(raw.emotional_impact, enumOptions.EmotionType),
        title: raw.title || raw.name || item.name,
      });
      return;
    }

    if (type === 'plot_device') {
      payload.plot_devices.push({
        ...raw,
        title: raw.title || raw.name || item.name,
      });
      return;
    }

    if (type === 'dialogue') {
      const content = String(raw.content || raw.text || raw.line || '');
      payload.dialogues.push({
        ...raw,
        emotion: normalizeEnumValue(raw.emotion, enumOptions.EmotionType),
        speaker: raw.speaker || raw.name || item.name,
        content,
        text: content,
      });
    }
  });

  payload.connections = connections.map((item) => ({
    id: String(item.id || createLocalId('connection')),
    source_id: String(item.sourceId || item.source_id || ''),
    target_id: String(item.targetId || item.target_id || ''),
    relationship_type: String(item.relationshipType || item.relationship_type || 'influences'),
    semantic_family: String(
      item.semanticFamily || item.semantic_family || inferConnectionSemanticFamily(item.relationshipType)
    ),
    description: String(item.description || ''),
    strength: Math.max(1, Number(item.strength) || 1),
  }));

  const primaryWorld = payload.worlds[0];
  if (primaryWorld || worldviewText.trim()) {
    payload.worldview = {
      ...(primaryWorld || {
        id: 'worldview_primary',
        name: worldviewText.trim() || '世界观',
      }),
      premise: worldviewText.trim(),
    };
  }

  return payload;
};

const themeAtomsFromText = (themesText: string) =>
  themesText
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);

const impactOrder = (type: string) => {
  const index = ORDER.indexOf(type);
  return index === -1 ? ORDER.length : index;
};

const sortImpactItems = <T extends { type: string; name: string }>(items: T[]) =>
  [...items].sort(
    (left, right) =>
      impactOrder(left.type) - impactOrder(right.type) || String(left.name || '').localeCompare(String(right.name || ''))
  );

const EMPTY_INSPECTOR_ENUMS: InspectorEnums = {
  CharacterType: [],
  ConflictType: [],
  ActType: [],
  BeatType: [],
  GenreType: [],
  EmotionType: [],
  WritingStyle: [],
  WritingTone: [],
};

const UI_RELEASE = `${APP_BRAND_NAME} · UI 2026.03.28`;
const INPUT_MAX_LENGTH = {
  worldview: 4000,
  themes: 2400,
  prompt: 5000,
};

const App: React.FC = () => {
  const [locale, setLocale] = useState<Locale>(() => resolveInitialLocale());
  const text = APP_TEXT[locale];
  const uiText = text as any;
  const meta = META_BY_LOCALE[locale];
  const brandFacetIntro =
    locale === 'zh'
      ? {
          eyebrow: '观察重点',
          summary: '下面三项是创作时经常需要反复对照的细部维度，因此保留在顶部，但不再做成强操作按钮。',
        }
      : {
          eyebrow: 'Reading lenses',
          summary:
            'These three lenses stay near the top because they are compared often during writing, but they are no longer presented as primary actions.',
        };
  const surfaceModes = VIEW_MODE_META[locale];
  const dropdownText =
    locale === 'zh'
      ? {
          create: '展开元素类型',
          script: '展开剧本文本',
          relations: '展开关联关系',
        }
      : {
          create: 'Expand element types',
          script: 'Expand script body',
          relations: 'Expand relationships',
        };
  const ownerText =
    locale === 'zh'
      ? {
          trigger: 'Owner 登录',
          passwordLabel: 'Owner 密码',
          passwordPlaceholder: '输入 owner 密码',
          login: '进入管理',
          hint: '仅管理员可管理访问码。',
          manage: '访问码管理',
          adminBadge: '管理',
          ownerHashHint: '在网址后追加 #owner，可以显示 owner 登录入口。',
          loginSuccess: '已进入 owner 会话',
          loginFailed: 'Owner 登录失败',
          loadFailed: '加载访问码失败',
          createSuccess: (count: number) => `已创建 ${count} 条访问码`,
          createFailed: '创建访问码失败',
          toggleFailed: '更新访问码状态失败',
          quantity: '数量',
          maxUses: '每码次数',
          prefix: '前缀',
          notes: '备注',
          notesPlaceholder: '例如：编辑团队',
          refresh: '刷新',
          create: '生成',
          usage: '使用情况',
          status: '状态',
          actions: '操作',
          active: '启用',
          disabled: '停用',
          disable: '停用',
          enable: '启用',
          empty: '当前还没有访问码。',
        }
      : {
          trigger: 'Owner login',
          passwordLabel: 'Owner password',
          passwordPlaceholder: 'Enter the owner password',
          login: 'Open admin',
          hint: 'Only administrators can manage access codes.',
          manage: 'Invite-code admin',
          adminBadge: 'Admin',
          ownerHashHint: 'Append #owner to the URL to reveal the owner login entry.',
          loginSuccess: 'Administrator sign in succeeded',
          loginFailed: 'Owner login failed',
          loadFailed: 'Failed to load invite codes',
          createSuccess: (count: number) => `${count} invite codes created`,
          createFailed: 'Failed to create invite codes',
          toggleFailed: 'Failed to update invite code',
          quantity: 'Quantity',
          maxUses: 'Uses per code',
          prefix: 'Prefix',
          notes: 'Notes',
          notesPlaceholder: 'For example: editorial team',
          refresh: 'Refresh',
          create: 'Create codes',
          usage: 'Usage',
          status: 'Status',
          actions: 'Actions',
          active: 'Active',
          disabled: 'Disabled',
          disable: 'Disable',
          enable: 'Enable',
          empty: 'No invite codes yet.',
        };
  const impactText =
    locale === 'zh'
      ? {
          eyebrow: '蝴蝶效应',
          title: '结构回响',
          clean: '工作图此刻安静。下一次改写扇动翅膀时，这里会显出将被惊动的结构范围。',
          liveEyebrow: '显式提示',
          liveTitle: '你刚刚扇动了一次翅膀',
          liveNote: (changed: number, affected: number) =>
            `这一笔已经惊动 ${changed} 个直接节点，并在画布深处牵出 ${affected} 处连锁回响。`,
          modeNote:
            '系统会先做结构 diff 和节点匹配，只重生成真正被惊动的 act / 集 / 场景子树；未受影响节点会被冻结，原 id 与排序尽量保留。',
          promptNote: '文风提示仍会改变整体气息，但真正的重生成范围由这次蝴蝶效应的传播路径决定。',
          uiOnly: '这次只改了画布布局，不会影响结构和剧本内容。',
          noStructural: '这次振翅还没有触及结构层。',
          changed: (count: number) => `振翅源点 ${count}`,
          affected: (count: number) => `回响范围 ${count}`,
          scenes: (count: number) => `场景 ${count}`,
          events: (count: number) => `事件 ${count}`,
          global: '全局气压',
          more: (count: number) => `还有 ${count} 项未展开`,
          connection: '连线',
          created: '新增',
          updated: '修改',
          deleted: '删除',
          linked: '沿结构传导',
          worldview: '世界观的改写会把回响带向整部剧本',
          themes: '主题的改写会把回响带向整部剧本',
          changedSection: (count: number) => `振翅源点 (${count})`,
          affectedSection: (count: number) => `结构回响 (${count})`,
        }
      : {
          eyebrow: 'Butterfly Effect',
          title: 'Structural Echo',
          clean: 'The board is quiet for now. When a new edit stirs the graph, this panel will reveal what wakes up.',
          liveEyebrow: 'Visible signal',
          liveTitle: 'A wingbeat just entered the graph',
          liveNote: (changed: number, affected: number) =>
            `This edit touched ${changed} direct nodes and has already started echoing across ${affected} downstream nodes.`,
          modeNote:
            'The system now runs a structure diff and node matching pass first. It only regenerates affected act / episode / scene subtrees, while untouched nodes stay frozen with their ids and order preserved as much as possible.',
          promptNote:
            'Prompt tone still colors the whole piece, but the actual rewrite scope now follows the butterfly path of this saved edit.',
          uiOnly: 'Only the canvas layout changed. Structure and script content stay the same.',
          noStructural: 'This wingbeat has not reached the structure layer.',
          changed: (count: number) => `Wingbeat sources ${count}`,
          affected: (count: number) => `Echo range ${count}`,
          scenes: (count: number) => `Scenes ${count}`,
          events: (count: number) => `Events ${count}`,
          global: 'Global pressure',
          more: (count: number) => `${count} more items not shown`,
          connection: 'Connection',
          created: 'Created',
          updated: 'Updated',
          deleted: 'Deleted',
          linked: 'Carried through structure',
          worldview: 'Worldview changes can send the echo across the entire script',
          themes: 'Theme changes can send the echo across the entire script',
          changedSection: (count: number) => `Wingbeat sources (${count})`,
          affectedSection: (count: number) => `Structural echoes (${count})`,
        };
  const scriptModeText =
    locale === 'zh'
      ? {
          note: '选择生成模式后，系统会先自动保存当前工作区。局部更新只重写受影响场景；全量重写会按当前结构重写全部场景；全局润色只优化文字节奏、衔接和语气，不改变结构事实。',
          partialLabel: '局部更新场景',
          fullLabel: '全量重写整剧',
          polishLabel: '全局润色文本',
          partialPending: '正在更新受影响场景',
          fullPending: '正在按结构重写整剧',
          polishPending: '正在全局润色场景文本',
          partialSuccess: '受影响场景已更新',
          fullSuccess: '整剧重写完成',
          polishSuccess: '全局润色完成',
          partialRequiresImpact: '局部更新仅在“保存结构改动”后可用。请先修改结构并保存，形成受影响场景。',
          actionFailed: (message: string) => `生成失败：${message}`,
        }
      : {
          note: 'Choosing a generation mode now auto-saves the current workspace first. Partial update rewrites only affected scenes; full rewrite rewrites every scene from the current structure; polish only improves wording, rhythm, and transitions without changing structure facts.',
          partialLabel: 'Update affected scenes',
          fullLabel: 'Rewrite full script',
          polishLabel: 'Global polish only',
          partialPending: 'Updating affected scenes',
          fullPending: 'Rewriting the full script from structure',
          polishPending: 'Polishing scene text globally',
          partialSuccess: 'Affected scenes updated',
          fullSuccess: 'Full script rewrite completed',
          polishSuccess: 'Global polish completed',
          partialRequiresImpact:
            'Partial update becomes available after you save structural edits that produce affected scenes.',
          actionFailed: (message: string) => `Generation failed: ${message}`,
        };
  const queueText =
    locale === 'zh'
      ? {
          structureQueued: '结构生成任务已加入后台队列',
          scriptQueued: '剧本生成任务已加入后台队列',
          queueRunning: '任务运行中',
          queueTimeout: '后台任务等待超时，请稍后刷新页面查看结果。',
          queueStalled: '后台任务已超过 10 分钟没有心跳，请刷新后重试。',
          missingRunId: '后台队列没有返回任务编号。',
        }
      : {
          structureQueued: 'The structure job has been queued.',
          scriptQueued: 'The script job has been queued.',
          queueRunning: 'Job is alive',
          queueTimeout: 'The background job timed out. Refresh the page later to check the result.',
          queueStalled: 'The background job has not reported a heartbeat for 10 minutes. Refresh and retry.',
          missingRunId: 'The queue did not return a run id.',
        };
  const replayText =
    locale === 'zh'
      ? {
          eyebrow: '最近生成任务',
          title: '参考与风险审计',
          empty: '这里会列出最近的结构或剧本生成任务。选中一条后，可以查看当次参考模式、原创力度和相似性风险。',
          loading: '正在载入任务详情...',
          noTrace: '这次生成没有参考痕迹。',
          requestConfig: '当次请求',
          resultTrace: '参考痕迹',
          similarity: '相似性审计',
          selectedReferences: '参考源',
          selectedAtoms: '混合原子',
          originalityRequirements: '显式原创约束',
          antiCopy: '反抄约束',
          mitigation: '建议动作',
          recentRuns: '任务列表',
          noRuns: '还没有生成记录。',
          summary: '上面这列是最近的生成任务。点击任一条目，可查看这次生成用了什么参考、施加了什么原创约束，以及风险分。',
          novelty: '原创力度',
          referenceMode: '参考模式',
          targetEpisodes: '目标集数',
          score: '风险分',
          variationDistance: '变体距离',
          traceMissing: '结果里还没有可展示的 trace。',
          runLoading: '加载中',
          modeLabels: {
            disabled: '关闭',
            auto: '自动',
            manual: '手动',
            mixed: '混合',
          } as Record<string, string>,
          riskLabels: {
            low: '低风险',
            medium: '中风险',
            high: '高风险',
          } as Record<string, string>,
          statusLabels: {
            completed: '已完成',
            running: '运行中',
            queued: '排队中',
            failed: '失败',
          } as Record<string, string>,
          typeLabels: {
            structure: '结构生成',
            script_full: '整剧重写',
            script_partial: '局部更新',
            script_polish: '全局润色',
          } as Record<string, string>,
          atomLabels: {
            hook_bundle: '钩子',
            relationship_bundle: '关系引擎',
            conflict_bundle: '冲突引擎',
            device_bundle: '装置',
            reveal_bundle: '揭示节奏',
            ending_bundle: '结局模式',
            theme_bundle: '主题',
          } as Record<string, string>,
          variationLabels: {
            role_reversal: '角色反转距离',
            reveal_order_variant: '揭示顺序变体距离',
            ending_mode_variant: '结局模式变体距离',
          } as Record<string, string>,
          dimensionLabels: {
            role_configuration: '角色配置',
            structural_scale: '结构尺度',
            event_sequence: '事件序列',
            reveal_order: '揭示顺序',
            climax_shape: '高潮形状',
            copy_marker_overlap: '特征重合',
          } as Record<string, string>,
        }
      : {
          eyebrow: 'Recent generation tasks',
          title: 'Reference and Risk Audit',
          empty: 'This panel lists recent structure and script generation tasks. Select one to inspect its reference mode, novelty level, and similarity risk.',
          loading: 'Loading task details...',
          noTrace: 'There is no reference trace for this run.',
          requestConfig: 'Request',
          resultTrace: 'Reference trace',
          similarity: 'Similarity audit',
          selectedReferences: 'Selected references',
          selectedAtoms: 'Mixed atoms',
          originalityRequirements: 'Explicit originality constraints',
          antiCopy: 'Anti-copy constraints',
          mitigation: 'Suggested actions',
          recentRuns: 'Task list',
          noRuns: 'No generation runs yet.',
          summary: 'The list above shows recent generation tasks. Pick one to inspect what references were used, what originality constraints were applied, and how risky the result looks.',
          novelty: 'Novelty level',
          referenceMode: 'Reference mode',
          targetEpisodes: 'Target episodes',
          score: 'Risk score',
          variationDistance: 'Variation distance',
          traceMissing: 'This result does not expose a replay trace yet.',
          runLoading: 'Loading',
          modeLabels: {
            disabled: 'Off',
            auto: 'Auto',
            manual: 'Manual',
            mixed: 'Mixed',
          } as Record<string, string>,
          riskLabels: {
            low: 'Low risk',
            medium: 'Medium risk',
            high: 'High risk',
          } as Record<string, string>,
          statusLabels: {
            completed: 'Completed',
            running: 'Running',
            queued: 'Queued',
            failed: 'Failed',
          } as Record<string, string>,
          typeLabels: {
            structure: 'Structure',
            script_full: 'Full rewrite',
            script_partial: 'Partial update',
            script_polish: 'Polish only',
          } as Record<string, string>,
          atomLabels: {
            hook_bundle: 'Hooks',
            relationship_bundle: 'Relationship engine',
            conflict_bundle: 'Conflict engine',
            device_bundle: 'Devices',
            reveal_bundle: 'Reveal rhythm',
            ending_bundle: 'Ending modes',
            theme_bundle: 'Themes',
          } as Record<string, string>,
          variationLabels: {
            role_reversal: 'Role reversal distance',
            reveal_order_variant: 'Reveal-order distance',
            ending_mode_variant: 'Ending-mode distance',
          } as Record<string, string>,
          dimensionLabels: {
            role_configuration: 'Role configuration',
            structural_scale: 'Structural scale',
            event_sequence: 'Event sequence',
            reveal_order: 'Reveal order',
            climax_shape: 'Climax shape',
            copy_marker_overlap: 'Marker overlap',
          } as Record<string, string>,
        };
  const surfaceText =
    locale === 'zh'
      ? {
          eyebrow: '同一画布',
          focus: '当前焦点',
          visible: '当前筛选',
          units: '结构单元',
          voices: '人物与对白',
          noSelection: '未选中元素',
          unnamed: '未命名元素',
        }
      : {
          eyebrow: 'Shared surface',
          focus: 'Current focus',
          visible: 'Visible now',
          units: 'Structural units',
          voices: 'Cast and dialogue',
          noSelection: 'No element selected',
          unnamed: 'Unnamed element',
        };
  const styleText =
    locale === 'zh'
      ? {
          title: '对白风格',
          note: '这些字段会进入角色上下文，影响后续场景重写时的台词口吻和语气。',
          dialogueStyle: '自定义对白说明',
          dialoguePlaceholder: '例如：克制、锋利、带停顿感，短句多，像审讯时的压迫对话。',
          writingStyle: '写作风格预设',
          writingTone: '语气预设',
          resetJson: '重置 JSON',
          none: '未设置',
        }
      : {
          title: 'Dialogue style',
          note: 'These fields are injected into character context and influence tone and line delivery during future scene rewrites.',
          dialogueStyle: 'Custom dialogue brief',
          dialoguePlaceholder: 'For example: clipped, restrained, pauses often, sharp under pressure.',
          writingStyle: 'Writing style preset',
          writingTone: 'Tone preset',
          resetJson: 'Reset JSON',
          none: 'Not set',
        };
  const inspectorUiText =
    locale === 'zh'
      ? {
          resetDraft: '重置表单与 JSON',
          advancedJson: '高级 JSON',
        }
      : {
          resetDraft: 'Reset form and JSON',
          advancedJson: 'Advanced JSON',
        };
  const starterText =
    locale === 'zh'
      ? {
          open: '开始创建',
          emptyTitle: '从这里开始工作台',
          emptyCopy: '支持从空白开始、用预设向导生成可编辑种子，或直接载入已保存内容。',
          emptyAction: '打开启动器',
          resetConfirm: '这会用新的本地草稿替换当前未保存内容，继续吗？',
          blankReady: '已创建空白草稿，你现在可以手动添加人物、场景和装置。',
          presetReady: '预设草稿已写入，可继续修改后再生成结构。',
          snapshotLoaded: (name: string) => `已载入快照：${name}`,
          snapshotLoadFailed: (message: string) => `载入快照失败：${message}`,
        }
      : {
          open: 'Start',
          emptyTitle: 'Start the workspace here',
          emptyCopy: 'Begin from scratch, use a preset wizard to build an editable seed, or load saved work.',
          emptyAction: 'Open starter',
          resetConfirm: 'This will replace the current unsaved local draft. Continue?',
          blankReady: 'A blank draft is ready. You can now add characters, scenes, and devices manually.',
          presetReady: 'The preset seed has been applied. Keep editing before you generate structure.',
          snapshotLoaded: (name: string) => `Snapshot loaded: ${name}`,
          snapshotLoadFailed: (message: string) => `Snapshot loading failed: ${message}`,
        };

  const [elements, setElements] = useState<any[]>([]);
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [jsonDraft, setJsonDraft] = useState('{}');
  const [inspectorEnums, setInspectorEnums] = useState<InspectorEnums>(EMPTY_INSPECTOR_ENUMS);
  const [deviceTypes, setDeviceTypes] = useState<{
    type: string;
    expected_evidence?: string[];
    short_drama_function?: string;
    short_drama_tags?: string[];
    recommended_catharsis_types?: string[];
    catharsis_tag_pool?: string[];
    recommended_reversal_types?: string[];
    narrative_layer?: string;
    owner_agent?: string;
  }[]>([]);
  const [status, setStatus] = useState<string>(() => APP_TEXT[resolveInitialLocale()].loading);
  const [search, setSearch] = useState('');
  const [script, setScript] = useState<any>(null);
  const [continuityBusy, setContinuityBusy] = useState(false);
  const [aiText, setAiText] = useState('');
  const [busy, setBusy] = useState(false);
  const [workflowProgress, setWorkflowProgress] = useState<number | null>(null);
  const [showStructure, setShowStructure] = useState(false);
  const [showScript, setShowScript] = useState(false);
  // The studio is the product: return users directly to actionable work.
  const [showLanding, setShowLanding] = useState(false);
  const [showProjectHub, setShowProjectHub] = useState(() => !isAdaptationEntry(window.location.search));
  const [experiencePage, setExperiencePage] = useState<'audio' | 'research' | null>(null);
  const [showStarter, setShowStarter] = useState(false);
  const [showNovelAdaptation, setShowNovelAdaptation] = useState(() => isAdaptationEntry(window.location.search));
  const [worldview, setWorldview] = useState('');
  const [themes, setThemes] = useState('');
  const [prompt, setPrompt] = useState<string>(() => APP_TEXT[resolveInitialLocale()].defaultPrompt);
  const DEFAULT_TARGET_EPISODES = 80;
  const [targetEpisodeCount, setTargetEpisodeCount] = useState(DEFAULT_TARGET_EPISODES);
  const [dramaSeriesMode, setDramaSeriesMode] = useState<DramaSeriesMode>('short');
  const [workbenchMode, setWorkbenchMode] = useState<WorkbenchMode>('vertical-short');
  const [presetFacets, setPresetFacets] = useState<PresetFacets | null>(null);
  const [referenceMode, setReferenceMode] = useState<ReferenceMode>('disabled');
  const [noveltyLevel, setNoveltyLevel] = useState<NoveltyLevel>('medium');
  const [selectedReferenceIds, setSelectedReferenceIds] = useState<string[]>([]);
  const [errorText, setErrorText] = useState('');
  const [authState, setAuthState] = useState<any>(DEFAULT_AUTH_STATE);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedRunRecord, setSelectedRunRecord] = useState<any>(null);
  const [loadingRunRecord, setLoadingRunRecord] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [canvasLayout, setCanvasLayout] = useState<Record<string, CanvasPosition>>({});
  const [dirty, setDirty] = useState(false);
  const [committedPayload, setCommittedPayload] = useState<any>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminCodes, setAdminCodes] = useState<any[]>([]);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [newCodeQuantity, setNewCodeQuantity] = useState('10');
  const [newCodeMaxUses, setNewCodeMaxUses] = useState('1');
  const [newCodePrefix, setNewCodePrefix] = useState('LOOLOO-BETA');
  const [newCodeNotes, setNewCodeNotes] = useState('');
  const [starterSnapshots, setStarterSnapshots] = useState<any[]>([]);
  const [loadingStarterSnapshots, setLoadingStarterSnapshots] = useState(false);
  const [ownerEntryVisible, setOwnerEntryVisible] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.location.hash === '#owner';
  });

  const layoutStorageKey = useMemo(() => {
    const workspaceKey =
      authState?.user?.workspace_id || authState?.user?.workspaceId || authState?.user?.id || 'shared';
    return `${CANVAS_LAYOUT_STORAGE_PREFIX}.${workspaceKey}`;
  }, [authState?.user?.id, authState?.user?.workspace_id, authState?.user?.workspaceId]);
  const needsInviteCodeLogin = requiresInviteCode(authState);

  const selected = useMemo(
    () => elements.find((item) => item.id === selectedId) || null,
    [elements, selectedId]
  );

  const draftData = useMemo(() => {
    try {
      return JSON.parse(jsonDraft || '{}');
    } catch (_error) {
      return { ...(selected?.data || {}) };
    }
  }, [jsonDraft, selected]);

  const primaryNameLabel = useMemo(() => {
    if (!selected) {
      return text.nameLabel;
    }

    if (locale === 'zh') {
      const labels: Record<string, string> = {
        world_state: '世界名称',
        theme: '主题名称',
        character: '人物姓名',
        arc_milestone: '里程碑标题',
        scene: '场景标题',
        event: '事件标题',
        plot_device: '装置标题',
        dialogue: '对白标题',
      };
      return labels[selected.type] || text.nameLabel;
    }

    const labels: Record<string, string> = {
      world_state: 'World name',
      theme: 'Theme name',
      character: 'Character name',
      arc_milestone: 'Milestone title',
      scene: 'Scene title',
      event: 'Event title',
      plot_device: 'Device title',
      dialogue: 'Dialogue title',
    };
    return labels[selected.type] || text.nameLabel;
  }, [locale, selected, text.nameLabel]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return elements;
    }
    return elements.filter((item) =>
      `${item.name} ${item.type} ${JSON.stringify(item.data)}`.toLowerCase().includes(query)
    );
  }, [elements, search]);

  const visibleIds = useMemo(() => new Set(filtered.map((item) => item.id)), [filtered]);

  const visibleConnections = useMemo(
    () => connections.filter((item) => visibleIds.has(item.sourceId) && visibleIds.has(item.targetId)),
    [connections, visibleIds]
  );
  const shouldShowStarterCta = useMemo(
    () =>
      !busy &&
      elements.length === 0 &&
      connections.length === 0 &&
      !script &&
      !worldview.trim() &&
      !themes.trim(),
    [busy, connections.length, elements.length, script, themes, worldview]
  );
  const isAuthenticated = Boolean(authState.authenticated);
  const workspaceIntegrityInvalid = authState.workspaceIntegrity?.valid === false;
  const hasWorkspaceDraft = Boolean(
    elements.length || connections.length || worldview.trim() || themes.trim() || script
  );
  const hasScriptResult = Boolean(script);
  const hasStructuredContent = useMemo(
    () => elements.some((item) => item.type === 'scene' || item.type === 'event'),
    [elements]
  );
  const surfaceStats = useMemo(() => {
    const stats = {
      scenes: 0,
      events: 0,
      characters: 0,
      dialogues: 0,
      visible: filtered.length,
      total: elements.length,
    };
    elements.forEach((item) => {
      const type = norm(item.type);
      if (type === 'scene') stats.scenes += 1;
      else if (type === 'event') stats.events += 1;
      else if (type === 'character') stats.characters += 1;
      else if (type === 'dialogue') stats.dialogues += 1;
    });
    return stats;
  }, [elements, filtered.length]);
  const surfaceFocus = useMemo(() => {
    if (!selected) {
      return surfaceText.noSelection;
    }
    const typeLabel = meta[selected.type]?.label || selected.type;
    const rawName =
      String(selected.name || selected.data?.name || selected.data?.title || selected.data?.label || '').trim() ||
      surfaceText.unnamed;
    return `${typeLabel} · ${rawName}`;
  }, [meta, selected, surfaceText.noSelection, surfaceText.unnamed]);

  const selectedConnections = useMemo(() => {
    if (!selected) {
      return [];
    }
    return connections.filter((item) => item.sourceId === selected.id || item.targetId === selected.id);
  }, [connections, selected]);

  const story = useMemo(
    () => script?.story_text || script?.story_content || script?.content || script?.script_text || '',
    [script]
  );
  const hasSceneScriptBaseline = useMemo(
    () =>
      elements.some(
        (item) =>
          norm(item.type) === 'scene' && String(item.data?.script_text || item.data?.scriptText || '').trim()
      ),
    [elements]
  );
  const hasSavedAffectedSceneTargets = useMemo(() => {
    const impact = committedPayload?.workspace_meta?.impact_analysis || {};
    const affected = Array.isArray(impact?.affected_scene_ids) ? impact.affected_scene_ids : [];
    const direct = Array.isArray(impact?.direct_scene_ids) ? impact.direct_scene_ids : [];
    return affected.length > 0 || direct.length > 0;
  }, [committedPayload]);
  const themeAtoms = useMemo(() => themeAtomsFromText(themes), [themes]);
  const buildReferencePayload = () => ({
    reference_ids: selectedReferenceIds,
    reference_mode: referenceMode,
    novelty_level: noveltyLevel,
    genre_tags: presetFacets?.search_tags || [],
    target_formula: presetFacets ? Object.values(presetFacets.choice_labels || {}).filter(Boolean) : [],
    preset_facets: presetFacets || undefined,
  });

  const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

  const generationProgressText = (record: any) => {
    const title = String(record?.latest_event?.title || '').trim();
    const detail = String(record?.latest_event?.detail || '').trim();
    if (title && detail) {
      return `${title} · ${detail}`;
    }
    return title || detail || '';
  };

  const syncFromGenerationRecord = async (record: any) => {
    const result = record?.result_payload || {};
    const structuredPayload = result?.structured_story_payload;
    const latestScriptPayload = result?.latest_script;
    const hasStoryText = Boolean(
      String(
        result?.story_text || result?.story_content || result?.content || result?.script_text || latestScriptPayload?.script_text || ''
      ).trim()
    );
    let hydrated = false;

    if (structuredPayload && typeof structuredPayload === 'object') {
      hydrate(structuredPayload);
      hydrated = true;
    }

    if (latestScriptPayload && typeof latestScriptPayload === 'object') {
      setScript(latestScriptPayload);
    } else if (hasStoryText) {
      setScript(result);
    }

    if (!hydrated) {
      await loadLatest(true);
    }
    if (!hasStoryText && String(record?.generation_type || '').includes('script')) {
      await loadScript();
    }
  };

  const pollGenerationRun = async (
    runId: string,
    successMessage: string
  ) => {
    const startedAt = Date.now();
    const hardTimeoutMs = 2 * 60 * 60 * 1000;
    const staleTimeoutMs = 10 * 60 * 1000;
    let lastHeartbeatAt = Date.now();

    while (Date.now() - startedAt < hardTimeoutMs) {
      const response = await axios.get(`${API_BASE}/api/generation-runs/${runId}`);
      const record = response.data || {};

      if (record?.status === 'completed') {
        setWorkflowProgress(100);
        setStatus(successMessage);
        await syncFromGenerationRecord(record);
        await refreshAuth();
        return record;
      }

      if (record?.status === 'failed') {
        const progress = generationProgressText(record);
        throw new Error(record?.error_text || progress || text.unknownError);
      }

      const updatedAt = Date.parse(String(record?.updated_at || ''));
      if (Number.isFinite(updatedAt)) {
        lastHeartbeatAt = Math.max(lastHeartbeatAt, updatedAt);
      }
      const progress = generationProgressText(record);
      if (progress) {
        setStatus(record?.worker_alive ? `${progress} · ${queueText.queueRunning}` : progress);
      }
      if (Date.now() - lastHeartbeatAt > staleTimeoutMs) {
        throw new Error(queueText.queueStalled);
      }

      await sleep(2000);
    }

    throw new Error(queueText.queueTimeout);
  };

  const submitQueuedGeneration = async ({
    path,
    payload,
    queuedMessage,
    successMessage,
    failureFormatter,
    onQueued,
  }: {
    path: string;
    payload: any;
    queuedMessage: string;
    successMessage: string;
    failureFormatter: (message: string) => string;
    onQueued?: () => void;
  }) => {
    if (!authState?.authenticated) {
      setStatus(
        locale === 'zh'
          ? '请先登录，再执行结构或剧本生成。'
          : 'Log in with an invite code before running structure or script generation.'
      );
      return;
    }

    setBusy(true);
    setErrorText('');

    if (dirty) {
      setStatus(uiText.saveWorkspacePending);
      try {
        const saveResponse = await axios.post(`${API_BASE}/workspace/commit`, {
          payload: currentWorkspacePayload,
          workspace_meta: {
            ...(currentWorkspacePayload.workspace_meta || {}),
            worldview_text: worldview,
            themes_text: themes,
            canvas_layout: canvasLayout,
            target_episode_count: targetEpisodeCount > 0 ? targetEpisodeCount : undefined,
            drama_series_mode: dramaSeriesMode,
            routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
            preset_facets: presetFacets || undefined,
          },
          create_snapshot: true,
          snapshot_name: 'workspace_save',
        });
        hydrate(saveResponse.data?.drama || {});
      } catch (error: any) {
        const message = uiText.saveWorkspaceFailed(safeMessage(error));
        setErrorText(message);
        setStatus(message);
        setBusy(false);
        return;
      }
    }

    setStatus(queuedMessage);
    try {
      const response = await axios.post(`${API_BASE}${path}`, payload);
      const runId = String(response.data?.run_id || '').trim();
      if (!runId) {
        throw new Error(queueText.missingRunId);
      }
      onQueued?.();
      setStatus(generationProgressText(response.data) || queuedMessage);
      const record = await pollGenerationRun(runId, successMessage);
      if (record) {
        setSelectedRunId(String(record.id || runId));
        setSelectedRunRecord(record);
      }
    } catch (error: any) {
      const message = safeMessage(error);
      setErrorText(message);
      setStatus(failureFormatter(message));
    } finally {
      setBusy(false);
    }
  };

  const canvasElements = useMemo(() => {
    const counters: Record<string, number> = {};

    return filtered.map((item) => {
      const type = norm(item.type);
      const slot = counters[type] || 0;
      counters[type] = slot + 1;

      return {
        ...item,
        position: canvasLayout[item.id] || defaultCanvasPosition(item, slot),
      };
    });
  }, [canvasLayout, filtered]);
  const currentWorkspacePayload = useMemo(
    () =>
      buildWorkspacePayload(
        elements,
        connections,
        worldview,
        themes,
        canvasLayout,
        targetEpisodeCount,
        dramaSeriesMode,
        presetFacets,
        inspectorEnums
      ),
    [canvasLayout, connections, dramaSeriesMode, elements, inspectorEnums, presetFacets, targetEpisodeCount, themes, worldview]
  );
  const liveImpactReport = useMemo(
    () => analyzeWorkspaceImpact(committedPayload, currentWorkspacePayload),
    [committedPayload, currentWorkspacePayload]
  );
  const savedImpactReport = useMemo(
    () => ({
      changed: Array.isArray(committedPayload?.workspace_meta?.impact_analysis?.changed)
        ? committedPayload.workspace_meta.impact_analysis.changed
        : [],
      affected: Array.isArray(committedPayload?.workspace_meta?.impact_analysis?.affected)
        ? committedPayload.workspace_meta.impact_analysis.affected
        : [],
      globalReasons: Array.isArray(committedPayload?.workspace_meta?.impact_analysis?.global_reasons)
        ? committedPayload.workspace_meta.impact_analysis.global_reasons
        : Array.isArray(committedPayload?.workspace_meta?.impact_analysis?.globalReasons)
          ? committedPayload.workspace_meta.impact_analysis.globalReasons
          : [],
      uiOnlyChanges: Boolean(
        committedPayload?.workspace_meta?.impact_analysis?.ui_only_changes ??
          committedPayload?.workspace_meta?.impact_analysis?.uiOnlyChanges
      ),
    }),
    [committedPayload]
  );
  const impactReport = liveImpactReport;
  const displayImpactReport = dirty ? null : savedImpactReport;
  const assistantContextSize = useMemo(() => {
    try {
      return JSON.stringify(currentWorkspacePayload || {}).length;
    } catch (_error) {
      return 0;
    }
  }, [currentWorkspacePayload]);
  const previewChangedImpactItems = useMemo(() => sortImpactItems(liveImpactReport.changed), [liveImpactReport.changed]);
  const previewAffectedImpactItems = useMemo(() => sortImpactItems(liveImpactReport.affected), [liveImpactReport.affected]);
  const changedImpactItems = useMemo(
    () => sortImpactItems(displayImpactReport?.changed || []),
    [displayImpactReport]
  );
  const affectedImpactItems = useMemo(
    () => sortImpactItems(displayImpactReport?.affected || []),
    [displayImpactReport]
  );
  const impactStats = useMemo(() => {
    const combined = [...changedImpactItems, ...affectedImpactItems];
    const countByType = (type: string) =>
      new Set(
        combined
          .filter((item) => item.type === type)
          .map((item: any) => String(item.id || item.name || ''))
          .filter(Boolean)
      ).size;
    return {
      scenes: countByType('scene'),
      events: countByType('event'),
    };
  }, [affectedImpactItems, changedImpactItems]);
  const modelingOverview = useMemo(
    () => [
      {
        label: locale === 'zh' ? '结构单元' : 'Structure units',
        value: String(surfaceStats.scenes + surfaceStats.events),
      },
      {
        label: locale === 'zh' ? '角色 / 对白' : 'Cast / dialogue',
        value: `${surfaceStats.characters} / ${surfaceStats.dialogues}`,
      },
      {
        label: locale === 'zh' ? '主题 / 连线' : 'Themes / links',
        value: `${themeAtoms.length} / ${connections.length}`,
      },
    ],
    [connections.length, locale, surfaceStats.characters, surfaceStats.dialogues, surfaceStats.events, surfaceStats.scenes, themeAtoms.length]
  );
  const filterOverview = useMemo(
    () => [
      {
        label: locale === 'zh' ? '当前可见' : 'Visible now',
        value: `${filtered.length} / ${elements.length}`,
      },
      {
        label: locale === 'zh' ? '可见连线' : 'Visible links',
        value: String(visibleConnections.length),
      },
      {
        label: locale === 'zh' ? '当前焦点' : 'Current focus',
        value: selected ? meta[selected.type]?.label || selected.type : locale === 'zh' ? '未选中' : 'No selection',
      },
    ],
    [elements.length, filtered.length, locale, meta, selected, visibleConnections.length]
  );
  const selectedTypeLabel = useMemo(() => {
    if (!selected) {
      return '';
    }
    return meta[selected.type]?.label || selected.type;
  }, [meta, selected]);
  const selectedFieldCount = useMemo(() => {
    if (!selected) {
      return 0;
    }
    return Object.values(draftData || {}).filter((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      return value !== undefined && value !== null && String(value).trim() !== '';
    }).length;
  }, [draftData, selected]);
  const selectedInspectorTags = useMemo(() => {
    if (!selected) {
      return [];
    }
    const tags: string[] = [];
    const act = selected.data?.act || selected.data?.act_type || selected.data?.actType;
    const episode = selected.data?.episode || selected.data?.episode_number || selected.data?.episodeNumber;
    const sceneRef = selected.data?.scene_id || selected.data?.sceneId;
    const speaker = selected.data?.speaker;

    if (selectedTypeLabel) {
      tags.push(selectedTypeLabel);
    }
    if (act) {
      tags.push(locale === 'zh' ? `幕 ${act}` : `Act ${act}`);
    }
    if (episode) {
      tags.push(locale === 'zh' ? `集 ${episode}` : `Episode ${episode}`);
    }
    if (sceneRef) {
      tags.push(locale === 'zh' ? `场景 ${sceneRef}` : `Scene ${sceneRef}`);
    }
    if (speaker) {
      tags.push(locale === 'zh' ? `说话者 ${speaker}` : `Speaker ${speaker}`);
    }

    return tags.slice(0, 5);
  }, [locale, selected, selectedTypeLabel]);
  const inspectorOverview = useMemo(() => {
    if (!selected) {
      return '';
    }
    if (locale === 'zh') {
      return dirty
        ? `当前元素有 ${selectedConnections.length} 条关联、${selectedFieldCount} 个已填写字段。你正在这张工作图里触发一场蝴蝶效应，右侧改动会被显式追踪。`
        : `当前元素有 ${selectedConnections.length} 条关联、${selectedFieldCount} 个已填写字段。右侧修改会直接作用于这张工作图。`;
    }
    return dirty
      ? `This element currently carries ${selectedConnections.length} relationships and ${selectedFieldCount} populated fields. You are triggering a visible butterfly effect, and edits here are being traced across the graph.`
      : `This element currently carries ${selectedConnections.length} relationships and ${selectedFieldCount} populated fields. Edits here apply directly to the working graph.`;
  }, [dirty, locale, selected, selectedConnections.length, selectedFieldCount]);
  const inspectorOverviewStats = useMemo(
    () => [
      {
        label: locale === 'zh' ? '关系' : 'Relations',
        value: String(selectedConnections.length),
      },
      {
        label: locale === 'zh' ? '字段' : 'Fields',
        value: String(selectedFieldCount),
      },
      {
        label: locale === 'zh' ? '类型' : 'Type',
        value: selectedTypeLabel || (locale === 'zh' ? '未选中' : 'Unselected'),
      },
    ],
    [locale, selectedConnections.length, selectedFieldCount, selectedTypeLabel]
  );

  const safeMessage = (error: any) => {
    const payload = error?.response?.data;
    const issue = Array.isArray(payload?.issues) ? payload.issues[0] : null;
    if (issue?.field && issue?.value !== undefined) {
      return `${payload?.error || error?.message || text.unknownError} (${issue.field}: ${issue.value})`;
    }
    return payload?.error || error?.message || text.unknownError;
  };

  const saveBlob = (blob: Blob, fallbackName: string, disposition?: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filenameFromDisposition(disposition) || fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const downloadFile = async (path: string, fallbackName: string) => {
    const response = await axios.get(`${API_BASE}${path}`, {
      responseType: 'blob',
    });
    const file = new Blob([response.data], {
      type: response.data?.type || 'application/octet-stream',
    });
    saveBlob(file, fallbackName, response.headers?.['content-disposition']);
  };

  const hydrate = (payload: any) => {
    const next = fromPayload(payload, meta, text.unnamedElement);
    setCommittedPayload(payload || {});
    setElements(next.elements);
    setConnections(next.connections);
    setSelectedId((current) => (current && next.elements.some((item) => item.id === current) ? current : null));
    setDirty(false);

    const workspaceMeta = payload?.workspace_meta || payload?.workspaceMeta || {};
    const nextLayout = normalizeCanvasLayout(workspaceMeta?.canvas_layout || workspaceMeta?.canvasLayout);
    const needsReflow = shouldReflowCanvasLayout(next.elements, nextLayout);
    setCanvasLayout(buildAutoCanvasLayout(next.elements, needsReflow ? {} : nextLayout, { overwrite: needsReflow }));

    const rawWorldview =
      payload?.worldview || payload?.world_state || payload?.worldState || firstEntry(payload?.worlds);
    const worldviewSeed =
      typeof rawWorldview === 'string'
        ? rawWorldview
        : rawWorldview?.premise || rawWorldview?.description || rawWorldview?.summary || rawWorldview?.name || '';
    const workspaceWorldview = typeof workspaceMeta?.worldview_text === 'string' ? workspaceMeta.worldview_text : '';
    setWorldview(workspaceWorldview || String(worldviewSeed || ''));

    const themeSeed = arr(payload?.themes)
      .map((item: any) => item?.title || item?.name || item?.description || '')
      .map((item: string) => item.trim())
      .filter(Boolean)
      .join('\n');
    const workspaceThemes = typeof workspaceMeta?.themes_text === 'string' ? workspaceMeta.themes_text : '';
    setThemes(workspaceThemes || themeSeed || '');
    const nextEpisodeTarget = Number(workspaceMeta?.target_episode_count || payload?.target_episode_count || 0);
    setTargetEpisodeCount(Number.isFinite(nextEpisodeTarget) && nextEpisodeTarget > 0 ? nextEpisodeTarget : 0);
    const nextDramaModeRaw = String(workspaceMeta?.drama_series_mode || workspaceMeta?.routine || '').toLowerCase();
    setDramaSeriesMode(nextDramaModeRaw.includes('long') ? 'long' : 'short');
    const nextPresetFacets = workspaceMeta?.preset_facets;
    setPresetFacets(nextPresetFacets && typeof nextPresetFacets === 'object' ? nextPresetFacets : null);
  };

  const requestCurrentWorkspace = async () => {
    const response = await axios.get(`${API_BASE}/snapshot/download?mode=current`, {
      timeout: STARTUP_REQUEST_TIMEOUT_MS,
    });
    return response.data && typeof response.data === 'object' ? response.data : {};
  };

  const hasWorkspaceContent = (payload: any) => {
    if (!payload || typeof payload !== 'object' || Object.keys(payload).length === 0) {
      return false;
    }

    const preview = fromPayload(payload, meta, text.unnamedElement);
    return (
      preview.elements.length > 0 ||
      preview.connections.length > 0 ||
      Boolean(payload?.worldview || payload?.world_state || payload?.worldState || firstEntry(payload?.worlds))
    );
  };

  const loadFallback = async () => {
    const [worldRes, characterRes, themeRes] = await Promise.allSettled([
      axios.get(`${API_BASE}/worldview`),
      axios.get(`${API_BASE}/characters`),
      axios.get(`${API_BASE}/themes`),
    ]);

    hydrate({
      worldview: worldRes.status === 'fulfilled' ? worldRes.value.data?.worldview || {} : {},
      characters: characterRes.status === 'fulfilled' ? characterRes.value.data?.characters || [] : [],
      themes: themeRes.status === 'fulfilled' ? themeRes.value.data?.themes || [] : [],
    });
  };

  const loadLatest = async (force = false) => {
    if (dirty && !force && !window.confirm(uiText.saveBeforeRefresh)) {
      return;
    }

    setBusy(true);
    try {
      const workspacePayload = await requestCurrentWorkspace();
      if (hasWorkspaceContent(workspacePayload)) {
        hydrate(workspacePayload);
        setStatus(locale === 'zh' ? '已刷新当前工作区' : 'Workspace refreshed');
        return;
      }

      const list = await axios.get(`${API_BASE}/snapshot/list`);
      const latest = list.data?.snapshots?.[0];
      if (latest?.id) {
        const response = await axios.post(`${API_BASE}/snapshot/load`, { snapshot_id: latest.id });
        hydrate(response.data?.drama);
        setStatus(text.loadedLatestSnapshot(latest.name || latest.id));
      } else {
        await loadFallback();
        setStatus(text.loadedBackendFallback);
      }
    } catch (_error) {
      await loadFallback();
      setStatus(text.snapshotFallbackError);
    } finally {
      setBusy(false);
    }
  };

  const loadScript = async () => {
    if (dirty && !window.confirm(uiText.saveBeforeRefresh)) {
      return;
    }
    try {
      const response = await axios.get(`${API_BASE}/api/load-script`);
      const data = response.data?.data || response.data?.result || response.data;
      if (data && typeof data === 'object') {
        setScript(data);
        const structuredPayload = data?.structured_story_payload;
        if (structuredPayload && typeof structuredPayload === 'object') {
          hydrate(structuredPayload);
        } else if (hasWorkspaceContent(data)) {
          hydrate(data);
        }
        setStatus(text.loadedScript);
      } else {
        setStatus(text.noScriptBody);
      }
    } catch (_error) {
      setStatus(text.noSavedScript);
    }
  };

  const resetLocalDraft = (next?: {
    worldview?: string;
    themes?: string;
    prompt?: string;
    targetEpisodeCount?: number;
    dramaSeriesMode?: DramaSeriesMode;
    presetFacets?: PresetFacets | null;
  }) => {
    setElements([]);
    setConnections([]);
    setSelectedId(null);
    setScript(null);
    setCanvasLayout({});
    setWorldview(next?.worldview || '');
    setThemes(next?.themes || '');
    setPrompt(next?.prompt || APP_TEXT[locale].defaultPrompt);
    setTargetEpisodeCount(
      typeof next?.targetEpisodeCount === 'number' && next.targetEpisodeCount > 0 ? next.targetEpisodeCount : DEFAULT_TARGET_EPISODES
    );
    setDramaSeriesMode(next?.dramaSeriesMode || 'short');
    setPresetFacets(next?.presetFacets || null);
    setReferenceMode('disabled');
    setSelectedReferenceIds([]);
    setNoveltyLevel('medium');
    setErrorText('');
    setShowScript(false);
    setCommittedPayload(committedPayload || {});
  };

  const startBlankDraft = () => {
    if ((dirty || elements.length || script) && !window.confirm(starterText.resetConfirm)) {
      return;
    }
    resetLocalDraft();
    setDirty(true);
    setShowStarter(false);
    setStatus(starterText.blankReady);
  };

  const applyPresetDraft = (seed: PresetSeed) => {
    if ((dirty || elements.length || script) && !window.confirm(starterText.resetConfirm)) {
      return;
    }
    const seedElements = buildPresetSeedElements({
      seed,
      locale,
      meta,
      unnamedLabel: text.unnamedElement,
      createItemName: text.createItemName,
      enumOptions: inspectorEnums,
      deviceTypes,
    });
    resetLocalDraft({
      worldview: seed.worldview,
      themes: seed.themes,
      prompt: seed.prompt,
      targetEpisodeCount: seed.targetEpisodeCount,
      dramaSeriesMode: seed.dramaSeriesMode || 'short',
      presetFacets: seed.presetFacets || null,
    });
    setElements(seedElements);
    setCanvasLayout(buildAutoCanvasLayout(seedElements, {}, { overwrite: true }));
    setSelectedId(seedElements[0]?.id || null);
    setDirty(true);
    setShowStarter(false);
    setShowStructure(true);
    setStatus(starterText.presetReady);
  };

  const loadSnapshotById = async (snapshotId: number | string) => {
    if (dirty && !window.confirm(uiText.saveBeforeRefresh)) {
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(`${API_BASE}/snapshot/load`, { snapshot_id: snapshotId });
      hydrate(response.data?.drama);
      const snapshot = starterSnapshots.find((item) => String(item.id) === String(snapshotId));
      setShowStarter(false);
      setStatus(starterText.snapshotLoaded(snapshot?.name || String(snapshotId)));
    } catch (error: any) {
      const message = starterText.snapshotLoadFailed(safeMessage(error));
      setErrorText(message);
      setStatus(message);
    } finally {
      setBusy(false);
    }
  };

  const downloadCurrentSnapshot = async () => {
    if (dirty) {
      setStatus(uiText.saveBeforeGenerate);
      return;
    }
    setBusy(true);
    try {
      await downloadFile('/snapshot/download?mode=current', 'snapshot_workspace.json');
      setStatus(text.snapshotDownloadStarted);
    } catch (error: any) {
      setStatus(text.downloadFailed(safeMessage(error)));
    } finally {
      setBusy(false);
    }
  };

  const commitWorkspace = async () => {
    setBusy(true);
    setErrorText('');
    setStatus(uiText.saveWorkspacePending);
    try {
      const response = await axios.post(`${API_BASE}/workspace/commit`, {
        payload: currentWorkspacePayload,
        workspace_meta: {
          ...(currentWorkspacePayload.workspace_meta || {}),
          worldview_text: worldview,
          themes_text: themes,
          canvas_layout: canvasLayout,
          target_episode_count: targetEpisodeCount > 0 ? targetEpisodeCount : undefined,
          drama_series_mode: dramaSeriesMode,
          routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
          preset_facets: presetFacets || undefined,
        },
        create_snapshot: true,
        snapshot_name: 'workspace_save',
      });
      hydrate(response.data?.drama || {});
      setStatus(uiText.saveWorkspaceSuccess);
    } catch (error: any) {
      const message = uiText.saveWorkspaceFailed(safeMessage(error));
      setErrorText(message);
      setStatus(message);
    } finally {
      setBusy(false);
    }
  };

  const downloadLatestScript = async (format: 'txt' | 'json' = 'txt') => {
    setBusy(true);
    try {
      await downloadFile(`/api/download-script?format=${format}`, `script.${format}`);
      setStatus(text.scriptDownloadStarted);
    } catch (error: any) {
      setStatus(text.downloadFailed(safeMessage(error)));
    } finally {
      setBusy(false);
    }
  };

  const refreshAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/status`, {
        timeout: STARTUP_REQUEST_TIMEOUT_MS,
      });
      setAuthState({
        authenticated: Boolean(response.data?.authenticated),
        user: response.data?.user || null,
        providers: response.data?.providers || DEFAULT_AUTH_STATE.providers,
        inviteCodeRequired: Boolean(response.data?.invite_code_required ?? response.data?.inviteCodeRequired),
        inviteCodeLabel: response.data?.invite_code_label || response.data?.inviteCodeLabel || DEFAULT_AUTH_STATE.inviteCodeLabel,
        recentRuns: response.data?.recentRuns || response.data?.recent_runs || [],
        workspaceIntegrity: response.data?.workspace_integrity || DEFAULT_AUTH_STATE.workspaceIntegrity,
      });
    } catch (_error) {
      setAuthState(DEFAULT_AUTH_STATE);
    }
  };

  const formatRunType = (value: string) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'script_full' || normalized === 'script_generate') {
      return replayText.typeLabels.script_full;
    }
    if (normalized === 'script_partial' || normalized === 'script_generate_partial') {
      return replayText.typeLabels.script_partial;
    }
    if (normalized === 'script_polish') {
      return replayText.typeLabels.script_polish;
    }
    return replayText.typeLabels[normalized] || normalized || replayText.runLoading;
  };

  const formatRunStatus = (value: string) => {
    const normalized = String(value || '').trim().toLowerCase();
    return replayText.statusLabels[normalized] || normalized || replayText.runLoading;
  };

  const runStatusTone = (value: string) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'completed') {
      return 'is-completed';
    }
    if (normalized === 'running') {
      return 'is-running';
    }
    if (normalized === 'queued') {
      return 'is-queued';
    }
    if (normalized === 'failed') {
      return 'is-failed';
    }
    return 'is-idle';
  };

  const formatReplayTimestamp = (value: any) => {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }
    return date.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const loadGenerationRunDetail = async (runId: string) => {
    setLoadingRunRecord(true);
    try {
      const response = await axios.get(`${API_BASE}/api/generation-runs/${runId}`, {
        timeout: STARTUP_REQUEST_TIMEOUT_MS,
      });
      setSelectedRunRecord(response.data || null);
    } catch (_error) {
      setSelectedRunRecord(null);
    } finally {
      setLoadingRunRecord(false);
    }
  };

  const loginWithWeChat = () => {
    const query = inviteCode.trim() ? `&invite_code=${encodeURIComponent(inviteCode.trim())}` : '';
    window.location.href = `${API_BASE}/auth/wechat/start?next=/${query}`;
  };

  const loginGuest = async () => {
    setBusy(true);
    try {
      const loginPath = guestLoginPath(authState);
      await axios.post(`${API_BASE}${loginPath}`, inviteCode.trim() ? { invite_code: inviteCode.trim() } : {});
      await refreshAuth();
      await loadLatest(true);
      await loadScript();
      setStatus(text.guestLoginSuccess);
    } catch (error: any) {
      setStatus(text.loginFailed(safeMessage(error)));
    } finally {
      setBusy(false);
    }
  };

  const loginWithInviteCode = async () => {
    if (!inviteCode.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/invite-code-login`, { invite_code: inviteCode.trim() });
      await refreshAuth();
      await loadLatest(true);
      await loadScript();
      setStatus(text.guestLoginSuccess);
    } catch (error: any) {
      setStatus(text.loginFailed(safeMessage(error)));
    } finally {
      setBusy(false);
    }
  };

  const loginOwner = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/owner-login`, {
        password: ownerPassword,
      });
      setOwnerPassword('');
      await refreshAuth();
      await loadLatest(true);
      await loadScript();
      setStatus(ownerText.loginSuccess);
    } catch (error: any) {
      setStatus(`${ownerText.loginFailed}: ${safeMessage(error)}`);
    } finally {
      setBusy(false);
    }
  };

  const loadAdminCodes = async () => {
    setAdminBusy(true);
    setAdminError('');
    try {
      const response = await axios.get(`${API_BASE}/auth/admin/invite-codes`);
      setAdminCodes(response.data?.invite_codes || []);
    } catch (error: any) {
      setAdminError(`${ownerText.loadFailed}: ${safeMessage(error)}`);
    } finally {
      setAdminBusy(false);
    }
  };

  const createAdminCodes = async () => {
    setAdminBusy(true);
    setAdminError('');
    try {
      const response = await axios.post(`${API_BASE}/auth/admin/invite-codes/create`, {
        quantity: Number(newCodeQuantity) || 1,
        max_uses: Number(newCodeMaxUses) || 1,
        prefix: newCodePrefix.trim() || 'LOOLOO-BETA',
        notes: newCodeNotes.trim() || undefined,
      });
      const created = response.data?.created || [];
      setStatus(ownerText.createSuccess(created.length || 0));
      setNewCodeNotes('');
      await loadAdminCodes();
    } catch (error: any) {
      const message = `${ownerText.createFailed}: ${safeMessage(error)}`;
      setAdminError(message);
      setStatus(message);
    } finally {
      setAdminBusy(false);
    }
  };

  const toggleAdminCode = async (code: string, active: boolean) => {
    setAdminBusy(true);
    setAdminError('');
    try {
      await axios.patch(`${API_BASE}/auth/admin/invite-codes/${encodeURIComponent(code)}`, {
        active,
      });
      await loadAdminCodes();
    } catch (error: any) {
      const message = `${ownerText.toggleFailed}: ${safeMessage(error)}`;
      setAdminError(message);
      setStatus(message);
      setAdminBusy(false);
    }
  };

  const logout = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/logout`);
      setScript(null);
      setShowAdminPanel(false);
      await refreshAuth();
      await loadLatest(true);
      setStatus(text.logoutSuccess);
    } catch (error: any) {
      setStatus(text.logoutFailed(safeMessage(error)));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const status = await axios.get(`${API_BASE}/auth/status`, { timeout: STARTUP_REQUEST_TIMEOUT_MS });
        if (!status.data?.authenticated && status.data?.providers?.guest && !status.data?.invite_code_required) {
          await axios.post(`${API_BASE}/auth/guest-login`, {});
        }
      } catch (_error) {
        // The status panel will show the connection problem after refresh.
      }
      await refreshAuth();
      await loadLatest(true);
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (String(process.env.REACT_APP_ENABLE_SOCKET_WEBSOCKET || '').trim() !== '1') {
      return undefined;
    }
    const socket = io(API_BASE, {
      // Polling sessions can land on different Gunicorn workers and lose their
      // Engine.IO sid. A single proxied WebSocket keeps progress on one worker.
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 10000,
      reconnectionDelayMax: 20000,
    });
    socket.on('connect_error', (err: any) => {
      // Silently suppress repeated WebSocket errors in production
      // where Caddy may block websocket handshake (P2)
    });
    socket.on('workflow_status', (payload: any) => {
      const current = payload?.current;
      if (current?.title) {
        const detail = current.detail || current.summary || '';
        setStatus(detail ? `${current.title} · ${detail}` : current.title);
      }
      if (typeof current?.progress === 'number') {
        setWorkflowProgress(current.progress);
      }
    });
    return () => {
      socket.close();
    };
  }, []);

  useEffect(() => {
    persistLocale(locale);
    document.documentElement.lang = locale;
    setPrompt((current) => current === APP_TEXT.zh.defaultPrompt || current === APP_TEXT.en.defaultPrompt ? APP_TEXT[locale].defaultPrompt : current);
  }, [locale]);

  useEffect(() => {
    const recentRuns = authState.recentRuns || [];
    if (recentRuns.length === 0) {
      setSelectedRunId(null);
      setSelectedRunRecord(null);
      return;
    }
    if (selectedRunId && recentRuns.some((run: any) => String(run.id || '') === selectedRunId)) {
      return;
    }
    setSelectedRunId(String(recentRuns[0]?.id || ''));
  }, [authState.recentRuns, selectedRunId]);

  useEffect(() => {
    if (!selectedRunId) {
      return;
    }
    if (String(selectedRunRecord?.id || '') === selectedRunId && selectedRunRecord?.status !== 'running') {
      return;
    }
    void loadGenerationRunDetail(selectedRunId);
  }, [selectedRunId, selectedRunRecord]);

  useEffect(() => {
    if (showLanding) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showLanding]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleHashChange = () => {
      setOwnerEntryVisible(window.location.hash === '#owner');
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    setCanvasLayout(readCanvasLayout(layoutStorageKey));
  }, [layoutStorageKey]);

  useEffect(() => {
    writeCanvasLayout(layoutStorageKey, canvasLayout);
  }, [canvasLayout, layoutStorageKey]);

  useEffect(() => {
    setCanvasLayout((current) => {
      const next = buildAutoCanvasLayout(elements, current, { overwrite: false });
      let changed = false;

      elements.forEach((item) => {
        const currentPosition = current[item.id];
        const nextPosition = next[item.id];
        if (
          !currentPosition ||
          !nextPosition ||
          currentPosition.x !== nextPosition.x ||
          currentPosition.y !== nextPosition.y
        ) {
          changed = true;
        }
      });

      Object.keys(next).forEach((id) => {
        if (!elements.some((item) => item.id === id)) {
          delete next[id];
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [elements]);

  useEffect(() => {
    if (!selected) {
      setNameDraft('');
      setJsonDraft('{}');
      setErrorText('');
      return;
    }
    setNameDraft(selected.name);
    setJsonDraft(JSON.stringify(selected.data || {}, null, 2));
    setErrorText('');
  }, [selected]);

  useEffect(() => {
    if (showAdminPanel && authState.user?.is_admin) {
      loadAdminCodes();
    }
  }, [authState.user?.is_admin, showAdminPanel]);

  useEffect(() => {
    let cancelled = false;

    const loadStarterSnapshots = async () => {
      if (!showStarter) {
        return;
      }
      setLoadingStarterSnapshots(true);
      try {
        const response = await axios.get(`${API_BASE}/snapshot/list`);
        if (!cancelled) {
          setStarterSnapshots(response.data?.snapshots || []);
        }
      } catch (_error) {
        if (!cancelled) {
          setStarterSnapshots([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingStarterSnapshots(false);
        }
      }
    };

    loadStarterSnapshots();
    return () => {
      cancelled = true;
    };
  }, [showStarter]);

  useEffect(() => {
    let cancelled = false;

    const loadEnums = async () => {
      try {
        const [enumResponse, deviceTypeResponse] = await Promise.all([
          axios.get(`${API_BASE}/api/enums`),
          axios.get(`${API_BASE}/plot_devices/types`),
        ]);
        if (!cancelled) {
          setInspectorEnums({
            CharacterType: enumResponse.data?.CharacterType || [],
            ConflictType: enumResponse.data?.ConflictType || [],
            ActType: enumResponse.data?.ActType || [],
            BeatType: enumResponse.data?.BeatType || [],
            GenreType: enumResponse.data?.GenreType || [],
            EmotionType: enumResponse.data?.EmotionType || [],
            WritingStyle: enumResponse.data?.WritingStyle || [],
            WritingTone: enumResponse.data?.WritingTone || [],
          });
          setDeviceTypes(deviceTypeResponse.data?.types || []);
        }
      } catch (error) {
        if (!cancelled) {
          setInspectorEnums(EMPTY_INSPECTOR_ENUMS);
          setDeviceTypes([]);
        }
      }
    };

    loadEnums();
    return () => {
      cancelled = true;
    };
  }, []);

  const markDirty = (message?: string) => {
    setDirty(true);
    if (message) {
      setStatus(message);
    }
  };

  const formatImpactReason = (reason: string) => {
    if (reason === 'created') {
      return impactText.created;
    }
    if (reason === 'updated') {
      return impactText.updated;
    }
    if (reason === 'deleted') {
      return impactText.deleted;
    }
    if (reason === 'global:worldview') {
      return impactText.worldview;
    }
    if (reason === 'global:themes') {
      return impactText.themes;
    }
    if (reason.startsWith('linked:')) {
      return impactText.linked;
    }
    return impactText.updated;
  };

  const formatGlobalReason = (reason: string) => {
    if (reason === 'worldview') {
      return impactText.worldview;
    }
    if (reason === 'themes') {
      return impactText.themes;
    }
    return reason;
  };

  const updateElementState = (elementId: string, patch: Record<string, any>) => {
    setElements((current) =>
      current.map((item) => {
        if (item.id !== elementId) {
          return item;
        }

        const nextName =
          patch.name ||
          patch.title ||
          patch.scene_name ||
          patch.event_name ||
          patch.speaker ||
          item.name;

        return {
          ...item,
          name: String(nextName || item.name),
          data: {
            ...item.data,
            ...patch,
          },
        };
      })
    );
  };

  const createLocalElement = (type: string, draft: Record<string, any> = {}) => {
    const { label, item } = buildLocalElementCard(type, draft, {
      meta,
      unnamedLabel: text.unnamedElement,
      createItemName: text.createItemName,
      enumOptions: inspectorEnums,
      deviceTypes,
    });

    setElements((current) => [
      ...current,
      item,
    ]);
    setSelectedId(item.id);
    setErrorText('');
    markDirty(text.createItemDone(label));
    return item.id;
  };

  const patchElementById = async (
    elementId: string,
    patch: Record<string, any>,
    nextStatus?: { success: string; failure: string }
  ) => {
    if (!elements.some((entry) => entry.id === elementId)) {
      return;
    }

    try {
      updateElementState(elementId, patch);
      markDirty(nextStatus?.success || uiText.applyDraftSuccess);
    } catch (error: any) {
      if (nextStatus?.failure) {
        setStatus(`${nextStatus.failure}: ${safeMessage(error)}`);
      }
    }
  };

  const removeElementById = async (elementId: string) => {
    const item = elements.find((entry) => entry.id === elementId);

    if (!item || !window.confirm(text.deleteConfirm(item.name))) {
      return;
    }

    setErrorText('');
    try {
      setElements((current) => current.filter((entry) => entry.id !== elementId));
      setConnections((current) =>
        current.filter((entry) => entry.sourceId !== elementId && entry.targetId !== elementId)
      );
      setCanvasLayout((current) => {
        const next = { ...current };
        delete next[elementId];
        return next;
      });
      if (selectedId === elementId) {
        setSelectedId(null);
      }
      if (item.type === 'world_state') {
        setWorldview('');
      }
      markDirty(text.deleteSuccess);
    } catch (error: any) {
      const message = text.deleteFailed(safeMessage(error));
      setErrorText(message);
      setStatus(message);
    }
  };

  const createItem = async (type: string) => {
    try {
      createLocalElement(type);
    } catch (error: any) {
      setStatus(`${text.createElement}: ${safeMessage(error)}`);
    }
  };

  const handleDraftDataChange = (nextDraft: Record<string, any>) => {
    setJsonDraft(JSON.stringify(nextDraft || {}, null, 2));
    setErrorText('');
  };

  const saveSelected = async () => {
    if (!selected) {
      return;
    }

    setErrorText('');
    try {
      const payload: Record<string, any> = {
        ...JSON.parse(jsonDraft || '{}'),
        name: nameDraft || selected.name,
        title: nameDraft || selected.name,
      };
      updateElementState(selected.id, payload);
      markDirty(uiText.applyDraftSuccess);
    } catch (error: any) {
      setErrorText(text.saveFailed(safeMessage(error)));
    }
  };

  const removeSelected = async () => {
    if (!selected) {
      return;
    }

    setErrorText('');
    try {
      setSelectedId(null);
      await removeElementById(selected.id);
    } catch (error: any) {
      setErrorText(text.deleteFailed(safeMessage(error)));
    }
  };

  const autoArrangeCanvas = () => {
    setCanvasLayout(buildAutoCanvasLayout(elements, {}, { overwrite: true }));
    setDirty(true);
    setStatus(locale === 'zh' ? '已重新整理工作图' : 'Canvas rearranged');
  };

  const handleCanvasPositionUpdate = (elementId: string, position: CanvasPosition) => {
    setCanvasLayout((current) => ({
      ...current,
      [elementId]: {
        x: Math.max(0, Math.round(position.x)),
        y: Math.max(0, Math.round(position.y)),
      },
    }));
    setDirty(true);
  };

  const handleCanvasConnectionCreate = async (
    sourceId: string,
    targetId: string,
    relationshipType: string,
    options?: { semanticFamily?: string }
  ) => {
    try {
      const exists = connections.some(
        (entry) =>
          entry.sourceId === sourceId &&
          entry.targetId === targetId &&
          entry.relationshipType === relationshipType
      );
      if (exists) {
        setStatus(locale === 'zh' ? '这条连线已经存在' : 'Connection already exists');
        return;
      }
      const nextConnection = {
        id: createLocalId('connection'),
        sourceId: String(sourceId),
        targetId: String(targetId),
        relationshipType: String(relationshipType),
        semanticFamily: String(
          options?.semanticFamily || inferConnectionSemanticFamily(relationshipType)
        ),
        description: '',
        strength: 1,
      };

      setConnections((current) => [...current, nextConnection]);
      markDirty(locale === 'zh' ? '连线已加入草稿' : 'Connection added to draft');
    } catch (error: any) {
      setStatus(
        locale === 'zh'
          ? `连线创建失败: ${safeMessage(error)}`
          : `Connection failed: ${safeMessage(error)}`
      );
    }
  };

  const handleCanvasActUpdate = async (elementId: string, act: string) => {
    await patchElementById(elementId, { act }, {
      success: locale === 'zh' ? '已更新幕次' : 'Act updated',
      failure: locale === 'zh' ? '幕次更新失败' : 'Act update failed',
    });
  };

  const handleCanvasBeatUpdate = async (elementId: string, beat: string) => {
    await patchElementById(elementId, { beat }, {
      success: locale === 'zh' ? '已更新节拍' : 'Beat updated',
      failure: locale === 'zh' ? '节拍更新失败' : 'Beat update failed',
    });
  };

  const focusInspectorPanel = () => {
    window.requestAnimationFrame(() => {
      const inspectorCard = document.querySelector('.panel-card--inspector') as HTMLElement | null;
      inspectorCard?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      window.requestAnimationFrame(() => {
        const nameInput = document.getElementById('element-name') as HTMLInputElement | null;
        nameInput?.focus();
        nameInput?.select();
      });
    });
  };

  const handleCanvasElementEdit = (element: any) => {
    setSelectedId(element.id);
    focusInspectorPanel();
  };

  const targetEpisodeUi = locale === 'zh'
    ? {
        label: '目标集数',
        placeholder: '例如 18',
        hint: '用于原生分集结构生成；留空或 0 表示按默认结构尺度生成。',
      }
    : {
        label: 'Target episodes',
        placeholder: 'For example 18',
        hint: 'Used for native episodic structure generation. Leave empty or 0 for the default structure scale.',
      };

  const runStructure = async () => {
    const characterSeeds = elements
      .filter((item) => item.type === 'character')
      .map((item) => ({
        ...(item?.data || {}),
        id: item?.data?.id || item.id,
        name: item?.data?.name || item.name,
      }));
    await submitQueuedGeneration({
      path: '/structure/generate',
      payload: {
        ...currentWorkspacePayload,
        worldview,
        themes: themeAtoms,
        characters: characterSeeds,
        prompt: `${prompt}\n${locale === 'en' ? 'Write the creative content and screenplay in English. Keep JSON field names unchanged.' : '创作内容及剧本请用中文，JSON字段名保持约定。'}`,
        target_episode_count: targetEpisodeCount > 0 ? targetEpisodeCount : undefined,
        drama_series_mode: dramaSeriesMode,
        routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
        workspace_meta: {
          ...(currentWorkspacePayload.workspace_meta || {}),
          target_episode_count: targetEpisodeCount > 0 ? targetEpisodeCount : undefined,
          drama_series_mode: dramaSeriesMode,
          routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
          preset_facets: presetFacets || undefined,
        },
        ...buildReferencePayload(),
      },
      queuedMessage: queueText.structureQueued,
      successMessage: text.structureSuccess,
      failureFormatter: text.structureFailed,
      onQueued: () => setShowStructure(false),
    });
  };

  const runScriptMode = async (path: string, queuedMessage: string, successMessage: string) => {
    if (!hasStructuredContent) {
      setStatus(
        locale === 'zh'
          ? '请先执行“生成结构”，再执行“生成剧本”。'
          : 'Generate structure first, then generate script.'
      );
      setShowScript(false);
      setShowStructure(true);
      return;
    }
    await submitQueuedGeneration({
      path,
      payload: {
        ...currentWorkspacePayload,
        prompt: `${prompt}\n${locale === 'en' ? 'Write the creative content and screenplay in English. Keep JSON field names unchanged.' : '创作内容及剧本请用中文，JSON字段名保持约定。'}`,
        worldview,
        themes: themeAtoms,
        drama_series_mode: dramaSeriesMode,
        routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
        workspace_meta: {
          ...(currentWorkspacePayload.workspace_meta || {}),
          target_episode_count: targetEpisodeCount > 0 ? targetEpisodeCount : undefined,
          drama_series_mode: dramaSeriesMode,
          routine: dramaSeriesMode === 'short' ? 'short_drama' : 'long_drama',
          preset_facets: presetFacets || undefined,
        },
        ...buildReferencePayload(),
      },
      queuedMessage,
      successMessage,
      failureFormatter: scriptModeText.actionFailed,
      onQueued: () => setShowScript(false),
    });
  };

  const runScript = async () =>
    runScriptMode('/script/generate', queueText.scriptQueued, scriptModeText.fullSuccess);

  const runPartialScript = async () => {
    if (!hasSceneScriptBaseline) {
      setStatus(
        locale === 'zh'
          ? '当前没有可局部更新的场景正文，请先执行“全量重写整剧”。'
          : 'There is no scene-text baseline yet. Run a full rewrite first.'
      );
      return;
    }
    if (!hasSavedAffectedSceneTargets) {
      setStatus(scriptModeText.partialRequiresImpact);
      return;
    }
    runScriptMode('/script/generate-partial', queueText.scriptQueued, scriptModeText.partialSuccess);
  };

  const runPolishScript = async () => {
    if (!hasSceneScriptBaseline) {
      setStatus(
        locale === 'zh'
          ? '当前没有可润色的场景正文，请先生成剧本。'
          : 'There is no scene-text baseline to polish yet. Generate the script first.'
      );
      return;
    }
    runScriptMode('/script/polish', queueText.scriptQueued, scriptModeText.polishSuccess);
  };

  const runContinuityValidation = async () => {
    setContinuityBusy(true);
    try {
      const response = await axios.post(`${API_BASE}/api/continuity/validate`, {
        workspace_payload: currentWorkspacePayload,
        use_llm: true,
      });
      setScript((current: any) => ({
        ...(current || {}),
        continuity_report: response.data || null,
      }));
      setStatus(locale === 'zh' ? '已完成连贯性校验' : 'Continuity validation completed');
    } catch (error: any) {
      setStatus(
        locale === 'zh'
          ? `连贯性校验失败：${safeMessage(error)}`
          : `Continuity validation failed: ${safeMessage(error)}`
      );
    } finally {
      setContinuityBusy(false);
    }
  };

  const replayRecord = selectedRunRecord || null;
  const replayRequest = replayRecord?.request_payload || {};
  const replayResult = replayRecord?.result_payload || {};
  const continuityReport = script?.continuity_report || null;
  const continuitySummary = continuityReport?.summary || {};
  const continuityViolations = Array.isArray(continuityReport?.violations) ? continuityReport.violations : [];
  const blockingContinuityViolations = continuityViolations.filter((item: any) => item?.severity === 'error');
  const dramabenchReport = continuityReport?.dramabench || null;
  const dramabenchSummary = dramabenchReport?.summary || {};
  const dramabenchDimensions = Array.isArray(dramabenchReport?.dimensions) ? dramabenchReport.dimensions : [];
  const narrativeAnalysis = continuityReport
    ? {
        catharsis_analysis: continuityReport.catharsis_analysis,
        speech_act_analysis: continuityReport.speech_act_analysis,
        information_gap_analysis: continuityReport.information_gap_analysis,
      }
    : null;
  const continuityText =
    locale === 'zh'
      ? {
          title: '连贯性校验',
          validate: '校验连贯性',
          passed: '已通过',
          failed: '存在阻断错误',
          noReport: '当前剧本还没有连贯性校验结果。',
          errors: (count: number) => `错误 ${count}`,
          warnings: (count: number) => `警告 ${count}`,
          graph: (nodes: number, edges: number) => `图谱 ${nodes} 节点 / ${edges} 边`,
          empty: '没有发现需要展示的冲突。',
        }
      : {
          title: 'Continuity validation',
          validate: 'Validate continuity',
          passed: 'Passed',
          failed: 'Blocking issues found',
          noReport: 'There is no continuity report for the current script yet.',
          errors: (count: number) => `Errors ${count}`,
          warnings: (count: number) => `Warnings ${count}`,
          graph: (nodes: number, edges: number) => `Graph ${nodes} nodes / ${edges} edges`,
          empty: 'No conflicts to show.',
        };
  const dramabenchText =
    locale === 'zh'
      ? {
          title: 'DramaBench aligned view',
          overall: 'Overall',
          empty: 'No DramaBench-aligned report yet.',
          score: (value: number) => `${Math.round((Number(value) || 0) * 100)} / 100`,
        }
      : {
          title: 'DramaBench-aligned view',
          overall: 'Overall',
          empty: 'No DramaBench-aligned report yet.',
          score: (value: number) => `${Math.round((Number(value) || 0) * 100)} / 100`,
        };
  const replayTrace = replayResult?.reference_trace || {};
  const replaySimilarity = replayResult?.similarity_risk || replayTrace?.similarity_risk || {};
  const replaySelectedReferences = replayResult?.selected_references || replayTrace?.selected_references || [];
  const replaySelectedAtoms = replayTrace?.selected_atoms || {};
  const replayOriginalityRequirements = replayTrace?.originality_requirements || [];
  const replayAntiCopyConstraints = replayTrace?.anti_copy_constraints || [];
  const replayVariationDistance = replaySimilarity?.variation_distance || {};
  const replayReferenceScores = replaySimilarity?.reference_scores || [];
  const replayMitigation = replaySimilarity?.mitigation || [];
  const replayRiskLevel = String(replaySimilarity?.risk_level || '').trim().toLowerCase();
  const replayRiskPillClass =
    replayRiskLevel === 'high' ? 'is-warning' : replayRiskLevel === 'medium' ? 'is-idle' : 'is-online';

  const impactPanel = (
    <section className={`panel-card impact-card${dirty ? ' impact-card--butterfly' : ''}`}>
      <div className="panel-card__header">
        <div className="panel-title">
          <span className="panel-title__icon">
            <StudioIcon name="structure" />
          </span>
          <div>
            <div className="panel-card__eyebrow">{impactText.eyebrow}</div>
            <h2>{impactText.title}</h2>
          </div>
        </div>
      </div>

      {dirty && (
        <>
          <div className="butterfly-callout">
            <div className="butterfly-callout__copy">
              <span className="butterfly-callout__eyebrow">{locale === 'zh' ? '草稿预演' : 'Draft preview'}</span>
              <strong>{locale === 'zh' ? '蝴蝶尚未起飞' : 'The butterfly is still grounded'}</strong>
              <p>
                {locale === 'zh'
                  ? `当前这轮改动预估会触及 ${previewChangedImpactItems.length} 个直接节点，并可能波及 ${previewAffectedImpactItems.length} 个后续节点。只有点击“保存工作区”后，这个范围才会写入结构回响并被后端增量生成使用。`
                  : `This draft is currently previewing ${previewChangedImpactItems.length} direct nodes and ${previewAffectedImpactItems.length} downstream echoes. The range is only committed to incremental generation after you save the workspace.`}
              </p>
            </div>
            <div className="butterfly-callout__glyph" aria-hidden="true">
              <span className="butterfly-callout__wing butterfly-callout__wing--left" />
              <span className="butterfly-callout__body" />
              <span className="butterfly-callout__wing butterfly-callout__wing--right" />
            </div>
          </div>

          <div className="impact-metrics">
            <span className="app-pill is-warning">
              <StudioIcon name="quill" />
              <span>{impactText.changed(previewChangedImpactItems.length)}</span>
            </span>
            <span className="app-pill">
              <StudioIcon name="spark" />
              <span>{impactText.affected(previewAffectedImpactItems.length)}</span>
            </span>
          </div>

          <p className="impact-note">
            {locale === 'zh'
              ? '当前右侧只是草稿层预演，不会立刻改写结构或剧本。'
              : 'The panel is only previewing the draft right now. It does not rewrite structure or script immediately.'}
          </p>
          <p className="impact-note impact-note--muted">
            {locale === 'zh'
              ? '保存之后，系统才会把这次修改写成正式的蝴蝶效应范围，并据此决定局部更新的真实边界。'
              : 'After saving, the system turns this into the committed butterfly range and uses it as the real boundary for partial rewrites.'}
          </p>
        </>
      )}

      {!dirty && changedImpactItems.length === 0 && affectedImpactItems.length === 0 && savedImpactReport.globalReasons.length === 0 && (
        <p className="empty-copy">{impactText.clean}</p>
      )}

      {!dirty && (changedImpactItems.length > 0 || affectedImpactItems.length > 0 || savedImpactReport.globalReasons.length > 0) && (
        <>
          <div className="butterfly-callout">
            <div className="butterfly-callout__copy">
              <span className="butterfly-callout__eyebrow">{locale === 'zh' ? '已保存' : 'Saved'}</span>
              <strong>{locale === 'zh' ? '这次蝴蝶效应已经写入工作区' : 'This butterfly effect is now committed'}</strong>
              <p>
                {locale === 'zh'
                  ? `后端接下来会以这份已保存的范围为准。当前保存触及 ${changedImpactItems.length} 个直接节点，并登记了 ${affectedImpactItems.length} 个结构回响。`
                  : `The backend now uses this saved range. The last save touched ${changedImpactItems.length} direct nodes and registered ${affectedImpactItems.length} structural echoes.`}
              </p>
            </div>
          </div>

          <p className="impact-note">{impactText.modeNote}</p>
          <p className="impact-note impact-note--muted">{impactText.promptNote}</p>

          <div className="impact-metrics">
            <span className="app-pill is-warning">
              <StudioIcon name="quill" />
              <span>{impactText.changed(changedImpactItems.length)}</span>
            </span>
            <span className="app-pill">
              <StudioIcon name="spark" />
              <span>{impactText.affected(affectedImpactItems.length)}</span>
            </span>
            <span className="app-pill">
              <StudioIcon name="scene" />
              <span>{impactText.scenes(impactStats.scenes)}</span>
            </span>
            <span className="app-pill">
              <StudioIcon name="event" />
              <span>{impactText.events(impactStats.events)}</span>
            </span>
          </div>

          {savedImpactReport.globalReasons.length > 0 && (
            <div className="impact-global">
              <strong>{impactText.global}</strong>
              <div className="impact-global__pills">
                {savedImpactReport.globalReasons.map((reason: string) => (
                  <span key={reason} className="impact-reason impact-reason--global">
                    {formatGlobalReason(reason)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {savedImpactReport.uiOnlyChanges && <p className="empty-copy">{impactText.uiOnly}</p>}
          {!savedImpactReport.uiOnlyChanges &&
            changedImpactItems.length === 0 &&
            affectedImpactItems.length === 0 && <p className="empty-copy">{impactText.noStructural}</p>}

          {changedImpactItems.length > 0 && (
            <details className="panel-dropdown panel-dropdown--content" open>
              <summary className="panel-dropdown__summary">{impactText.changedSection(changedImpactItems.length)}</summary>
              <div className="panel-dropdown__body">
                <div className="impact-list">
                  {changedImpactItems.slice(0, 8).map((item: any) => (
                    <div key={String(item.key || item.id || item.name)} className="impact-row">
                      <div className="impact-row__title">
                        <span className="impact-type">
                          {item.type === 'connection' ? impactText.connection : meta[item.type]?.label || item.type}
                        </span>
                        <strong>{item.name}</strong>
                        {item.changeKind && (
                          <span className={`impact-change impact-change--${item.changeKind}`}>
                            {formatImpactReason(item.changeKind)}
                          </span>
                        )}
                      </div>
                      <div className="impact-row__reasons">
                        {item.reasons.slice(0, 2).map((reason: string) => (
                          <span key={`${String(item.key || item.id || item.name)}-${reason}`} className="impact-reason">
                            {formatImpactReason(reason)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {changedImpactItems.length > 8 && (
                    <div className="impact-row impact-row--more">{impactText.more(changedImpactItems.length - 8)}</div>
                  )}
                </div>
              </div>
            </details>
          )}

          {affectedImpactItems.length > 0 && (
            <details className="panel-dropdown panel-dropdown--content" open>
              <summary className="panel-dropdown__summary">
                {impactText.affectedSection(affectedImpactItems.length)}
              </summary>
              <div className="panel-dropdown__body">
                <div className="impact-list">
                  {affectedImpactItems.slice(0, 12).map((item: any) => (
                    <div key={String(item.key || item.id || item.name)} className="impact-row">
                      <div className="impact-row__title">
                        <span className="impact-type">
                          {item.type === 'connection' ? impactText.connection : meta[item.type]?.label || item.type}
                        </span>
                        <strong>{item.name}</strong>
                      </div>
                      <div className="impact-row__reasons">
                        {item.reasons.slice(0, 2).map((reason: string) => (
                          <span key={`${String(item.key || item.id || item.name)}-${reason}`} className="impact-reason">
                            {formatImpactReason(reason)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {affectedImpactItems.length > 12 && (
                    <div className="impact-row impact-row--more">{impactText.more(affectedImpactItems.length - 12)}</div>
                  )}
                </div>
              </div>
            </details>
          )}
        </>
      )}
    </section>
  );

  const inspectorPanel = (
    <section className="panel-card inspector-card panel-card--inspector">
      <div className="panel-card__header">
        <div className="panel-title">
          <span className="panel-title__icon">
            <StudioIcon name="inspector" />
          </span>
          <div>
            <div className="panel-card__eyebrow">{text.inspectorEyebrow}</div>
            <h2>{selected ? selected.name : text.inspectorEmptyTitle}</h2>
          </div>
        </div>
      </div>

      {!selected && (
        <div className="inspector-empty-state">
          <span className="inspector-empty-state__icon">
            <StudioIcon name="inspector" />
          </span>
          <p className="empty-copy">{text.inspectorEmptyCopy}</p>
        </div>
      )}

      {selected && (
        <>
          <div className="inspector-hero">
            <div className="inspector-hero__copy">
              <div className="panel-card__eyebrow">{selectedTypeLabel}</div>
              <strong>{selected.name}</strong>
              <p>{inspectorOverview}</p>
            </div>
            <div className="inspector-hero__stats">
              {inspectorOverviewStats.map((item) => (
                <div key={item.label} className="inspector-hero__stat">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>

          {selectedInspectorTags.length > 0 && (
            <div className="inspector-tags">
              {selectedInspectorTags.map((tag) => (
                <span key={tag} className="inspector-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="inspector-surface">
            <label className="form-label" htmlFor="element-name">
              {primaryNameLabel}
            </label>
            <input
              id="element-name"
              className="app-input"
              type="text"
              value={nameDraft}
              placeholder={primaryNameLabel}
              onChange={(event) => setNameDraft(event.target.value)}
            />
            <ElementInspectorFields
              locale={locale}
              selected={selected}
              elements={elements}
              draftData={draftData}
              enumOptions={inspectorEnums}
              deviceTypes={deviceTypes}
              onChangeDraft={handleDraftDataChange}
            />

            <details className="panel-dropdown inspector-json-panel">
              <summary className="panel-dropdown__summary">{inspectorUiText.advancedJson}</summary>
              <div className="panel-dropdown__body">
                <label className="form-label" htmlFor="element-json">
                  {text.jsonLabel}
                </label>
                <textarea
                  id="element-json"
                  className="app-textarea"
                  value={jsonDraft}
                  onChange={(event) => setJsonDraft(event.target.value)}
                />
              </div>
            </details>

            <div className="inspector-actions">
              <button className="app-button" type="button" onClick={saveSelected} disabled={busy}>
                <span className="app-button__content">
                  <StudioIcon name="spark" />
                  <span>{text.save}</span>
                </span>
              </button>
              <button
                className="app-button app-button--ghost"
                type="button"
                onClick={() => {
                  setJsonDraft(JSON.stringify(selected.data || {}, null, 2));
                }}
              >
                <span className="app-button__content">
                  <StudioIcon name="refresh" />
                  <span>{inspectorUiText.resetDraft}</span>
                </span>
              </button>
              <button className="app-button app-button--danger" type="button" onClick={removeSelected}>
                <span className="app-button__content">
                  <StudioIcon name="device" />
                  <span>{text.delete}</span>
                </span>
              </button>
            </div>
          </div>

          <details className="panel-dropdown panel-dropdown--content" open>
            <summary className="panel-dropdown__summary">
              {dropdownText.relations} ({selectedConnections.length})
            </summary>
            <div className="panel-dropdown__body">
              <div className="relation-list">
                {selectedConnections.length === 0 && <p className="empty-copy">{text.noRelations}</p>}
                {selectedConnections.map((item) => {
                  const peerId = item.sourceId === selected.id ? item.targetId : item.sourceId;
                  const peer = elements.find((entry) => entry.id === peerId);
                  return (
                    <div key={item.id} className="relation-row">
                      <strong>{item.relationshipType}</strong>
                      <span>{peer?.name || peerId}</span>
                      <p>
                        {item.semanticFamily ? `${item.semanticFamily} · ` : ''}
                        {item.description || text.noRelationDescription}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </details>

          {errorText && <div className="form-error">{errorText}</div>}
        </>
      )}
    </section>
  );

  const workflowText =
    locale === 'zh'
      ? {
          eyebrow: '工作流',
          title: '当前操作流',
          latestPipeline: '最新链路：工作区 -> 结构 -> 剧本',
          accountStep: '账户',
          workspaceStep: '工作区',
          generationStep: '生成',
          statusSignedIn: '已登录',
          statusSignedOut: '未登录',
          statusDraftReady: '已有草稿',
          statusDraftEmpty: '空白工作区',
          statusScriptReady: '已有剧本',
          nextTitleLoggedOut: '先登录，再进入持久化工作流',
          nextTitleEmpty: '先建一个工作区种子',
          nextTitleStructure: '先整理结构，再生成剧本',
          nextTitleScript: '继续生成、保存或回放结果',
          nextSummaryLoggedOut:
            '登录后，你可以在自己的工作区保存设定、剧本和修改记录。',
          nextSummaryEmpty:
            '当前工作区还是空白。优先用启动器从模板或空白稿开始，再进入结构和剧本生成。',
          nextSummaryStructure:
            '你已经有设定或节点，但结构内容还不完整。现在最合理的顺序是先生成结构，再扩展成剧本。',
          nextSummaryScript:
            '当前工作区已有可继续推进的内容。生成、载入和保存都保持在同一条工作链里。',
          authHint: '登录后即可保存工作区内容并使用生成工具。',
          invalidHint: '工作区存在无效枚举值，保存和生成已禁用。先修复或载入有效快照。',
          workspaceHint: '先至少准备一部分设定、角色或结构节点，再执行保存与生成。',
        }
      : {
          eyebrow: 'Workflow',
          title: 'Current flow',
          latestPipeline: 'Latest path: workspace -> structure -> script',
          accountStep: 'Account',
          workspaceStep: 'Workspace',
          generationStep: 'Generation',
          statusSignedIn: 'Signed in',
          statusSignedOut: 'Signed out',
          statusDraftReady: 'Draft ready',
          statusDraftEmpty: 'Empty workspace',
          statusScriptReady: 'Script ready',
          nextTitleLoggedOut: 'Sign in before using the persistent flow',
          nextTitleEmpty: 'Seed the workspace first',
          nextTitleStructure: 'Shape structure before generating script',
          nextTitleScript: 'Continue generating, saving, or replaying results',
          nextSummaryLoggedOut:
            'After invite-code login, refresh, save, load-script, and generation all stay tied to the same workspace. Logged-out actions should not pretend otherwise.',
          nextSummaryEmpty:
            'The workspace is still empty. Start from the starter with a template or blank draft before moving into structure and script generation.',
          nextSummaryStructure:
            'You already have setting or node material, but not enough structure yet. The sensible next move is structure first, script second.',
          nextSummaryScript:
            'The current workspace already contains material you can continue from. Generation, loading, and saving now belong to one consistent work chain.',
          authHint: 'When signed out, generation and persistence stay disabled so the UI matches real behavior.',
          invalidHint: 'The workspace contains invalid enum values. Save and generation are disabled until you fix or replace it.',
          workspaceHint: 'Prepare at least some setting, cast, or structure nodes before saving or generating.',
        };
  const nextWorkflowTitle = !isAuthenticated
    ? workflowText.nextTitleLoggedOut
    : shouldShowStarterCta
      ? workflowText.nextTitleEmpty
      : !hasStructuredContent
        ? workflowText.nextTitleStructure
        : workflowText.nextTitleScript;
  const nextWorkflowSummary = !isAuthenticated
    ? workflowText.nextSummaryLoggedOut
    : shouldShowStarterCta
      ? workflowText.nextSummaryEmpty
      : !hasStructuredContent
        ? workflowText.nextSummaryStructure
        : workflowText.nextSummaryScript;
  const workflowHint = workspaceIntegrityInvalid
    ? workflowText.invalidHint
    : !isAuthenticated
      ? workflowText.authHint
      : !hasWorkspaceDraft
        ? workflowText.workspaceHint
        : '';
  const canOpenStarter = !busy;
  const canLoadSavedScript = isAuthenticated && !busy;
  const canDownloadWorkspace = isAuthenticated && !busy && hasWorkspaceDraft;
  const canSaveWorkspace = isAuthenticated && !busy && !workspaceIntegrityInvalid && hasWorkspaceDraft;
  const canGenerateStructure = isAuthenticated && !busy && !workspaceIntegrityInvalid;
  const canGenerateScript = isAuthenticated && !busy && !workspaceIntegrityInvalid && hasWorkspaceDraft && hasStructuredContent;
  const workflowWorkspaceActions: Array<{
    key: string;
    label: string;
    icon: StudioIconName;
    tone: 'starter' | 'strong' | 'ghost';
    disabled: boolean;
    onClick: () => void;
  }> = [
    {
      key: 'novel-adaptation',
      label: locale === 'zh' ? '剧本改编 · 织梦' : 'Adapt with SceneWeave',
      icon: 'library',
      tone: 'starter',
      disabled: !isAuthenticated || busy,
      onClick: () => setShowNovelAdaptation(true),
    },
    {
      key: 'starter',
      label: shouldShowStarterCta ? starterText.emptyAction : starterText.open,
      icon: 'spark',
      tone: 'starter',
      disabled: !canOpenStarter,
      onClick: () => setShowStarter(true),
    },
    {
      key: 'load-script',
      label: text.loadExistingScript,
      icon: 'library',
      tone: 'ghost',
      disabled: !canLoadSavedScript,
      onClick: () => void loadScript(),
    },
    {
      key: 'download',
      label: text.downloadSnapshot,
      icon: 'download',
      tone: 'ghost',
      disabled: !canDownloadWorkspace,
      onClick: () => void downloadCurrentSnapshot(),
    },
    {
      key: 'save',
      label: uiText.saveWorkspace,
      icon: 'spark',
      tone: 'strong',
      disabled: !canSaveWorkspace,
      onClick: () => void commitWorkspace(),
    },
  ];
  const workflowGenerationActions: Array<{
    key: string;
    label: string;
    icon: StudioIconName;
    tone: 'strong' | 'ghost';
    disabled: boolean;
    onClick: () => void;
  }> = [
    {
      key: 'structure',
      label: text.generateStructure,
      icon: 'structure',
      tone: 'ghost',
      disabled: !canGenerateStructure,
      onClick: () => setShowStructure(true),
    },
    {
      key: 'script',
      label: text.generateScript,
      icon: 'script',
      tone: 'strong',
      disabled: !canGenerateScript,
      onClick: () => setShowScript(true),
    },
  ];
  const workbenchSteps = buildWorkbenchSteps(workbenchMode, {
    premiseReady: hasWorkspaceDraft,
    structureReady: hasStructuredContent,
    scriptReady: hasScriptResult,
  });

  const handleEntryAction = (action: EntryActionId) => {
    if (action === 'audio' || action === 'research') {
      setExperiencePage(action);
      setShowProjectHub(false);
      return;
    }
    if (action === 'novel') {
      setShowProjectHub(false);
      setShowNovelAdaptation(true);
      return;
    }
    if (action === 'review') {
      window.location.assign('/blind-review');
      return;
    }
    if (action === 'new-short' || action === 'new-film') {
      const isFilm = action === 'new-film';
      setWorkbenchMode(isFilm ? 'film' : 'vertical-short');
      setDramaSeriesMode(isFilm ? 'long' : 'short');
      setShowProjectHub(false);
      setShowStarter(true);
      return;
    }
    setShowProjectHub(false);
    if (action === 'advanced') setViewMode('canvas');
  };

  if (experiencePage) {
    return <ExperiencePage locale={locale} kind={experiencePage} onBack={() => { setExperiencePage(null); setShowProjectHub(true); }} />;
  }

  if (showProjectHub) {
    return (
      <ProjectHub
        locale={locale}
        onLocaleChange={setLocale}
        authenticated={isAuthenticated}
        hasDraft={hasWorkspaceDraft}
        recentRuns={authState.recentRuns || []}
        onAction={handleEntryAction}
      />
    );
  }

  return (
    <div className="app-shell">
      <div className="studio-byok-bar"><button type="button" onClick={() => setShowProjectHub(true)}>{locale === 'en' ? '← Home' : '← 首页'}</button><ModelKeyControl locale={locale} /></div>
      {showLanding && (
        <LandingHero
          locale={locale}
          onEnter={() => {
            setShowLanding(false);
            setTimeout(() => {
              const workbench = document.querySelector('.studio-os-body');
              if (workbench) {
                workbench.scrollIntoView({ behavior: 'smooth', block: 'start' });
              } else {
                window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
              }
            }, 100);
          }}
        />
      )}
      <StudioSidebar
        locale={locale}
        activeMode={viewMode}
        busy={busy}
        authenticated={isAuthenticated}
        dirty={dirty}
        elementsCount={elements.length}
        connectionsCount={connections.length}
        scriptReady={hasScriptResult}
        workflowStatus={status}
        modes={surfaceModes}
        onModeChange={setViewMode}
        onOpenStarter={() => setShowStarter(true)}
        onOpenStructure={() => setShowStructure(true)}
        onOpenScript={() => setShowScript(true)}
        onSaveWorkspace={() => void commitWorkspace()}
        onAuthenticate={() => void (isAuthenticated ? logout() : loginGuest())}
        inviteCodeEnabled={Boolean(authState.providers?.invite_code)}
        inviteCode={inviteCode}
        onInviteCodeChange={setInviteCode}
        onInviteLogin={() => void loginWithInviteCode()}
        canGenerateStructure={canGenerateStructure}
        canGenerateScript={canGenerateScript}
        canSaveWorkspace={canSaveWorkspace}
      />
      <div className="studio-os-body">
      <header className="app-topbar">
        <div className="app-brand">
          <div className="app-brand__masthead">
            <div className="app-brand__signature">
              <span className="app-brand__signature-mark">M</span>
              <div className="app-brand__signature-copy">
                <strong>{APP_BRAND_NAME}</strong>
                <span>{locale === 'zh' ? 'Narrative Studio / Script Workbench' : 'Narrative Studio / Script Workbench'}</span>
              </div>
            </div>
          </div>
          <div className="app-brand__hero">
            <div className="app-brand__lead">
              <span className="app-brand__mark">
                <StudioIcon name="quill" />
              </span>
              <div className="app-brand__copy">
                <div className="app-brand__eyebrow">{text.brandEyebrow}</div>
                <h1>{text.title}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="app-topbar__side">
          <div className="app-locale-switch">
            <span className="app-locale-switch__label">
              <StudioIcon name="world" />
              <span>{text.languageLabel}</span>
            </span>

            <div className="app-locale-switch__group">
              <button
                type="button"
                className={`app-locale-switch__button ${locale === 'zh' ? 'is-active' : ''}`}
                onClick={() => setLocale('zh')}
              >
                {text.languageChinese}
              </button>
              <button
                type="button"
                className={`app-locale-switch__button ${locale === 'en' ? 'is-active' : ''}`}
                onClick={() => setLocale('en')}
              >
                {text.languageEnglish}
              </button>
            </div>
          </div>

          <div className="app-auth-card">
            <div className="app-auth-card__head">
              <span className="panel-title__icon">
                <StudioIcon name="assistant" />
              </span>
              <div>
                <div className="panel-card__eyebrow">{text.accountEyebrow}</div>
                <h2>
                  {authState.authenticated
                    ? authState.user?.display_name || text.accountLoggedInFallback
                    : text.accountLoggedOutTitle}
                </h2>
              </div>
            </div>

            <p className="app-auth-card__copy">
              {authState.authenticated
                ? text.accountCopyLoggedIn(authState.user?.primary_provider || 'guest')
                : text.accountCopyLoggedOut}
            </p>

            {authState.authenticated && authState.workspaceIntegrity?.valid === false && (
              <div className="app-auth-card__hint">
                {locale === 'zh'
                  ? `当前工作区存在 ${Array.isArray(authState.workspaceIntegrity?.issues) ? authState.workspaceIntegrity.issues.length : 0} 处无效枚举值，系统已停止自动覆盖保存，请先载入有效快照或修正后再继续。`
                  : `The current workspace contains ${Array.isArray(authState.workspaceIntegrity?.issues) ? authState.workspaceIntegrity.issues.length : 0} invalid enum values. Automatic overwrite is disabled until you load a valid snapshot or fix them.`}
              </div>
            )}

            {!authState.authenticated && authState.providers?.owner && !ownerEntryVisible && (
              <div className="app-auth-card__hint">{ownerText.ownerHashHint}</div>
            )}

            {!authState.authenticated && authState.providers?.owner && ownerEntryVisible && (
              <>
                <label className="form-label" htmlFor="owner-password-input">
                  {ownerText.passwordLabel}
                </label>
                <input
                  id="owner-password-input"
                  className="app-input"
                  type="password"
                  value={ownerPassword}
                  onChange={(event) => setOwnerPassword(event.target.value)}
                  placeholder={ownerText.passwordPlaceholder}
                />
                <div className="app-auth-card__hint">{ownerText.hint}</div>
              </>
            )}

            {!authState.authenticated && needsInviteCodeLogin && (
              <>
                <label className="form-label" htmlFor="invite-code-input">
                  {text.inviteCodeLabel(authState.inviteCodeLabel)}
                </label>
                <input
                  id="invite-code-input"
                  className="app-input"
                  type="text"
                  value={inviteCode}
                  onChange={(event) => setInviteCode(event.target.value)}
                  placeholder={text.inviteCodePlaceholder(authState.inviteCodeLabel)}
                />
                <div className="app-auth-card__hint">
                  {text.inviteCodeHint(authState.inviteCodeLabel)}
                </div>
              </>
            )}

            <div className="app-auth-card__actions">
              {!authState.authenticated && authState.providers?.owner && ownerEntryVisible && (
                <button
                  className="app-button app-button--strong"
                  type="button"
                  onClick={loginOwner}
                  disabled={busy || !ownerPassword.trim()}
                >
                  <span className="app-button__content">
                    <StudioIcon name="assistant" />
                    <span>{ownerText.login}</span>
                  </span>
                </button>
              )}

              {!authState.authenticated && authState.providers?.wechat && (
                <button className="app-button app-button--strong" type="button" onClick={loginWithWeChat}>
                  <span className="app-button__content">
                    <StudioIcon name="spark" />
                    <span>{text.wechatLogin}</span>
                  </span>
                </button>
              )}

              {!authState.authenticated &&
                (authState.providers?.invite_code ?? authState.providers?.guest) && (
                <button
                  className="app-button app-button--ghost"
                  type="button"
                  onClick={loginGuest}
                  disabled={busy || (needsInviteCodeLogin && !inviteCode.trim())}
                >
                  <span className="app-button__content">
                    <StudioIcon name="library" />
                    <span>{text.guestLogin}</span>
                  </span>
                </button>
              )}

              {!authState.authenticated && !authState.providers?.wechat && (
                <div className="app-auth-card__hint">{text.wechatHint}</div>
              )}

              {authState.authenticated && (
                <>
                  {authState.user?.is_admin && (
                    <>
                      <span className="app-pill is-online">
                        <StudioIcon name="assistant" />
                        <span>{ownerText.adminBadge}</span>
                      </span>
                      <button
                        className="app-button app-button--strong"
                        type="button"
                        onClick={() => setShowAdminPanel(true)}
                        disabled={busy}
                      >
                        <span className="app-button__content">
                          <StudioIcon name="library" />
                          <span>{ownerText.manage}</span>
                        </span>
                      </button>
                    </>
                  )}
                  <span className="app-pill is-online">
                    <StudioIcon name="spark" />
                    <span>{text.recentRuns(authState.recentRuns?.length || 0)}</span>
                  </span>
                  <button className="app-button app-button--ghost" type="button" onClick={logout} disabled={busy}>
                    <span className="app-button__content">
                      <StudioIcon name="refresh" />
                      <span>{text.logout}</span>
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>

          <section className="panel-card app-action-card app-workflow-card">
            <div className="panel-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="structure" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{workflowText.eyebrow}</div>
                  <h2>{workflowText.title}</h2>
                </div>
              </div>
            </div>

            <div className="app-workflow-card__hero">
              <div className="app-workflow-card__copy">
                <div className="panel-card__eyebrow">{nextWorkflowTitle}</div>
                <strong>{nextWorkflowTitle}</strong>
                <p>{nextWorkflowSummary}</p>
              </div>
              <div className="app-workflow-card__chips">
                <span className={`app-pill ${isAuthenticated ? 'is-online' : 'is-idle'}`}>
                  <StudioIcon name="assistant" />
                  <span>{isAuthenticated ? workflowText.statusSignedIn : workflowText.statusSignedOut}</span>
                </span>
                <span className="app-pill">
                  <StudioIcon name="board" />
                  <span>{hasWorkspaceDraft ? workflowText.statusDraftReady : workflowText.statusDraftEmpty}</span>
                </span>
                <span className="app-pill">
                  <StudioIcon name="structure" />
                  <span>{workflowText.latestPipeline}</span>
                </span>
                {hasScriptResult && (
                  <span className="app-pill">
                    <StudioIcon name="script" />
                    <span>{workflowText.statusScriptReady}</span>
                  </span>
                )}
              </div>
            </div>

            <div className="app-workflow-card__grid">
              <section className="app-workflow-block">
                <div className="app-workflow-block__head">
                  <div className="panel-card__eyebrow">{workflowText.workspaceStep}</div>
                  <strong>{workflowText.workspaceStep}</strong>
                </div>
                <div className="app-workflow-actions">
                  {workflowWorkspaceActions.map((action) => (
                    <button
                      key={action.key}
                      className={`app-button ${
                        action.tone === 'starter'
                          ? 'app-button--starter'
                          : action.tone === 'strong'
                            ? 'app-button--strong'
                            : 'app-button--ghost'
                      }`}
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      <span className="app-button__content">
                        <StudioIcon name={action.icon} />
                        <span>{action.label}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>

              <section className="app-workflow-block">
                <div className="app-workflow-block__head">
                  <div className="panel-card__eyebrow">{workflowText.generationStep}</div>
                  <strong>{workflowText.generationStep}</strong>
                </div>
                <div className="app-workflow-actions app-workflow-actions--generation">
                  {workflowGenerationActions.map((action) => (
                    <button
                      key={action.key}
                      className={`app-button ${action.tone === 'strong' ? 'app-button--strong' : 'app-button--ghost'}`}
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      <span className="app-button__content">
                        <StudioIcon name={action.icon} />
                        <span>{action.label}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            </div>

            {workflowHint && <div className="app-workflow-card__hint">{workflowHint}</div>}
          </section>
        </div>
      </header>

      <nav className="workbench-command" aria-label={locale === 'zh' ? '创作流程' : 'Creation workflow'}>
        <div className="workbench-command__mode">
          <span>{locale === 'zh' ? '成品类型' : 'Format'}</span>
          <select
            value={workbenchMode}
            onChange={(event) => {
              const nextMode = event.target.value === 'film' ? 'film' : 'vertical-short';
              setWorkbenchMode(nextMode);
              setDramaSeriesMode(nextMode === 'film' ? 'long' : 'short');
            }}
          >
            <option value="vertical-short">{locale === 'zh' ? '竖屏短剧 · 优先' : 'Vertical short · Primary'}</option>
            <option value="film">{locale === 'zh' ? '电影剧本 · 进阶' : 'Film screenplay · Advanced'}</option>
          </select>
        </div>
        <ol className="workbench-command__steps">
          {workbenchSteps.map((step, index) => (
            <li key={step.id} className={`is-${step.status}`} title={step.description}>
              <span>{step.status === 'complete' ? '✓' : index + 1}</span>
              <strong>{step.label}</strong>
            </li>
          ))}
        </ol>
        <a className="app-button app-button--ghost workbench-command__review" href="/blind-review">
          {locale === 'zh' ? '分阶段质量评估' : 'Staged quality review'}
        </a>
      </nav>

      <section className="app-statusbar">
        <div className="app-statusbar__left">
          <span className={`app-pill ${busy ? 'is-online' : 'is-idle'}`}>
            <StudioIcon name={busy ? 'spark' : 'board'} />
            <span>{busy ? text.processing : text.ready}</span>
          </span>
          <span className="app-statusbar__text">{status}</span>
          {typeof workflowProgress === 'number' && busy && (
            <div className="app-statusbar__progress">
              <div
                className="app-statusbar__progress-bar"
                style={{ width: `${workflowProgress}%` }}
              />
            </div>
          )}
        </div>

        <div className="app-statusbar__meta">
          {dirty && (
            <span className="app-pill is-warning">
              <StudioIcon name="quill" />
              <span>{uiText.unsavedChanges}</span>
            </span>
          )}
          <span className="app-pill app-pill--release">
            <StudioIcon name="spark" />
            <span>{UI_RELEASE}</span>
          </span>
          <span>
            <StudioIcon name="board" />
            <span>{text.elementCount(elements.length)}</span>
          </span>
          <span>
            <StudioIcon name="spark" />
            <span>{text.connectionCount(connections.length)}</span>
          </span>
        </div>
      </section>

      <div className="app-grid">
        <aside className="app-panel app-panel--left">
          <section className="panel-card panel-card--modeling">
            <div className="panel-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="quill" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{text.modelingEyebrow}</div>
                  <h2>{text.createElement}</h2>
                </div>
              </div>
              <div className="panel-card__summary">
                <span>
                  {locale === 'zh'
                    ? '把设定、人物、场景和装置排成一条清晰的建模链，再落到同一张画布上。'
                    : 'Shape world, cast, scenes, and devices as one coherent modeling rail before they spill onto the canvas.'}
                </span>
              </div>
            </div>

            <div className="rail-hero">
              {modelingOverview.map((item) => (
                <div key={item.label} className="rail-hero__card">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <details className="panel-dropdown" open>
              <summary className="panel-dropdown__summary">{dropdownText.create}</summary>
              <div className="panel-dropdown__body">
                <div className="create-grid">
                  {ORDER.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className="create-tile"
                      onClick={() => createItem(type)}
                      disabled={busy}
                    >
                      <div className="create-tile__head">
                        <span className="create-tile__icon">
                          <StudioIcon name={meta[type].icon} />
                        </span>
                        <strong>{meta[type].label}</strong>
                      </div>
                      <span>{meta[type].desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </details>
          </section>

          <section className="panel-card panel-card--filter">
            <div className="panel-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="search" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{text.searchEyebrow}</div>
                  <h2>{text.filterView}</h2>
                </div>
              </div>
            </div>

            <div className="rail-search-meta">
              {filterOverview.map((item) => (
                <div key={item.label} className="rail-search-meta__card">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className="rail-search">
              <input
                className="app-input"
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={text.searchPlaceholder}
              />
              <div className="left-note">{text.leftNote}</div>
            </div>
          </section>
        </aside>

        <main className="app-main">
          <section className="app-panel app-panel--center">
            <div className="panel-card panel-card--board">
              <div className="panel-card__header panel-card__header--wide">
                <div className="panel-title">
                  <span className="panel-title__icon">
                    <StudioIcon name={surfaceModes[viewMode].icon} />
                  </span>
                  <div>
                    <div className="panel-card__eyebrow">{text.storyEyebrow}</div>
                    <h2>{surfaceModes[viewMode].label}</h2>
                  </div>
                </div>

                <div className="surface-toolbar">
                  <div className="panel-card__summary">
                    <span>{busy ? text.boardSummaryBusy : surfaceModes[viewMode].summary}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {viewMode === 'canvas' && (
                      <button
                        className="app-button app-button--ghost app-button--small"
                        type="button"
                        onClick={autoArrangeCanvas}
                      >
                        <span className="app-button__content">
                          <StudioIcon name="board" />
                          <span>{locale === 'zh' ? '自动整理' : 'Auto arrange'}</span>
                        </span>
                      </button>
                    )}

                    <div className="view-mode-dropdown">
                    <select 
                      className="view-mode-select"
                      value={viewMode}
                      onChange={(e) => setViewMode(e.target.value as ViewMode)}
                      aria-label={text.storyBoard}
                    >
                      {(['board', 'canvas', 'lanes'] as ViewMode[]).map((mode) => (
                        <option key={mode} value={mode}>
                          {surfaceModes[mode].label} - {surfaceModes[mode].hint}
                        </option>
                      ))}
                    </select>
                    <span className="view-mode-select__icon">
                      <StudioIcon name={surfaceModes[viewMode].icon} />
                    </span>
                  </div>
                  </div>
                </div>
              </div>

              <div className="surface-atlas">
                <div className="surface-atlas__lead">
                  <div className="panel-card__eyebrow">{surfaceText.eyebrow}</div>
                  <strong>{surfaceModes[viewMode].label}</strong>
                  <p>{surfaceModes[viewMode].summary}</p>
                </div>
                <div className="surface-atlas__grid">
                  <div className="surface-atlas__card">
                    <span>{surfaceText.focus}</span>
                    <strong>{surfaceFocus}</strong>
                  </div>
                  <div className="surface-atlas__card">
                    <span>{surfaceText.visible}</span>
                    <strong>
                      {surfaceStats.visible} / {surfaceStats.total}
                    </strong>
                  </div>
                  <div className="surface-atlas__card">
                    <span>{surfaceText.units}</span>
                    <strong>
                      {surfaceStats.scenes} / {surfaceStats.events}
                    </strong>
                  </div>
                  <div className="surface-atlas__card">
                    <span>{surfaceText.voices}</span>
                    <strong>
                      {surfaceStats.characters} / {surfaceStats.dialogues}
                    </strong>
                  </div>
                </div>
              </div>

              {shouldShowStarterCta && (
                <div className="workspace-empty-cta">
                  <div className="workspace-empty-cta__copy">
                    <div className="panel-card__eyebrow">{starterText.open}</div>
                    <h3>{starterText.emptyTitle}</h3>
                    <p>{starterText.emptyCopy}</p>
                  </div>
                  <button className="app-button app-button--starter" type="button" onClick={() => setShowStarter(true)}>
                    <span className="app-button__content">
                      <StudioIcon name="quill" />
                      <span>{starterText.emptyAction}</span>
                    </span>
                  </button>
                </div>
              )}

              <div className={`surface-frame surface-frame--${viewMode}`}>
                {viewMode === 'board' && (
                  <StudioBoard
                    elements={filtered}
                    connections={connections}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    locale={locale}
                  />
                )}

                {viewMode === 'canvas' && (
                  <DramaCanvas
                    locale={locale}
                    enumOptions={inspectorEnums}
                    dramaElements={canvasElements}
                    connections={visibleConnections}
                    selectedId={selectedId}
                    onElementSelect={setSelectedId}
                    onElementCreate={(type) => createItem(type)}
                    onConnectionCreate={handleCanvasConnectionCreate}
                    onSetAct={handleCanvasActUpdate}
                    onSetBeat={handleCanvasBeatUpdate}
                    onElementCreateFromDraft={(type, draft) => createLocalElement(type, draft)}
                    onElementPatch={(elementId, patch, statusMessage) =>
                      patchElementById(elementId, patch, {
                        success: statusMessage || uiText.applyDraftSuccess,
                        failure: locale === 'zh' ? '草稿更新失败' : 'Draft update failed',
                      })
                    }
                    onElementEdit={handleCanvasElementEdit}
                    onElementDelete={removeElementById}
                    onElementPositionUpdate={handleCanvasPositionUpdate}
                  />
                )}

                {viewMode === 'lanes' && (
                  <LaneView
                    locale={locale}
                    enumOptions={inspectorEnums}
                    dramaElements={filtered}
                    onElementClick={(element) => setSelectedId(element.id)}
                  />
                )}
              </div>
            </div>

            {script && (
              <section className="panel-card panel-card--script">
                <div className="panel-card__header panel-card__header--wide">
                  <div className="panel-title">
                    <span className="panel-title__icon">
                      <StudioIcon name="script" />
                    </span>
                    <div>
                      <div className="panel-card__eyebrow">{text.scriptEyebrow}</div>
                      <h2>{text.scriptResult}</h2>
                    </div>
                  </div>

                <div className="script-meta">
                  <span>{text.sceneCount(script?.structured_story_summary?.scene_count || 0)}</span>
                  <span>{text.eventCount(script?.structured_story_summary?.event_count || 0)}</span>
                  <span>{text.dialogueCount(script?.structured_story_summary?.dialogue_count || 0)}</span>
                </div>
              </div>

              <div className="panel-card__actions">
                <button
                  className="app-button app-button--ghost app-button--small"
                  type="button"
                  onClick={runContinuityValidation}
                  disabled={continuityBusy}
                >
                  <span className="app-button__content">
                    <StudioIcon name="spark" />
                    <span>{continuityBusy ? text.processing : continuityText.validate}</span>
                  </span>
                </button>
                <button
                  className="app-button app-button--ghost app-button--small"
                  type="button"
                  onClick={() => downloadLatestScript('txt')}
                >
                  <span className="app-button__content">
                    <StudioIcon name="download" />
                    <span>{text.downloadTxt}</span>
                  </span>
                </button>
                <button
                  className="app-button app-button--ghost app-button--small"
                  type="button"
                  onClick={() => downloadLatestScript('json')}
                >
                  <span className="app-button__content">
                    <StudioIcon name="library" />
                    <span>{text.downloadJson}</span>
                  </span>
                </button>
              </div>

              <details className="panel-dropdown panel-dropdown--content" open>
                <summary className="panel-dropdown__summary">{dropdownText.script}</summary>
                <div className="panel-dropdown__body">
                  <pre className="script-surface">{story || text.storyFallback}</pre>
                </div>
              </details>

              <details className="panel-dropdown panel-dropdown--content" open>
                <summary className="panel-dropdown__summary">{continuityText.title}</summary>
                <div className="panel-dropdown__body">
                  {!continuityReport ? (
                    <p className="empty-copy">{continuityText.noReport}</p>
                  ) : (
                    <div className="impact-list">
                      <div className="impact-metrics">
                        <span className={`app-pill ${blockingContinuityViolations.length > 0 ? 'is-warning' : 'is-online'}`}>
                          <StudioIcon name="spark" />
                          <span>{blockingContinuityViolations.length > 0 ? continuityText.failed : continuityText.passed}</span>
                        </span>
                        <span className="app-pill">
                          <StudioIcon name="structure" />
                          <span>{continuityText.errors(continuitySummary?.error_count || 0)}</span>
                        </span>
                        <span className="app-pill">
                          <StudioIcon name="library" />
                          <span>{continuityText.warnings(continuitySummary?.warning_count || 0)}</span>
                        </span>
                        <span className="app-pill">
                          <StudioIcon name="canvas" />
                          <span>{continuityText.graph(continuityReport?.graph?.node_count || 0, continuityReport?.graph?.edge_count || 0)}</span>
                        </span>
                      </div>
                      <details className="panel-dropdown panel-dropdown--content" open>
                        <summary className="panel-dropdown__summary">{dramabenchText.title}</summary>
                        <div className="panel-dropdown__body">
                          {!dramabenchReport ? (
                            <p className="empty-copy">{dramabenchText.empty}</p>
                          ) : (
                            <div className="impact-list">
                              <div className="impact-metrics">
                                <span className="app-pill">
                                  <StudioIcon name="library" />
                                  <span>
                                    {dramabenchText.overall}: {dramabenchText.score(dramabenchSummary?.overall_score || 0)}
                                  </span>
                                </span>
                                <span className="app-pill">
                                  <StudioIcon name="scene" />
                                  <span>{dramabenchSummary?.dimension_count || 0} dimensions</span>
                                </span>
                              </div>
                              {dramabenchDimensions.map((item: any) => (
                                <div key={String(item?.id || item?.name)} className="impact-row">
                                  <div className="impact-row__title">
                                    <span className="impact-type">{String(item?.status || '').toUpperCase()}</span>
                                    <strong>{item?.name || item?.id || 'dimension'}</strong>
                                  </div>
                                  <div className="impact-note">{dramabenchText.score(item?.score || 0)}</div>
                                  {Array.isArray(item?.findings) &&
                                    item.findings.slice(0, 2).map((finding: string) => (
                                      <div key={`${String(item?.id || item?.name)}-${finding}`} className="impact-note impact-note--muted">
                                        {finding}
                                      </div>
                                    ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </details>
                      {continuityViolations.length === 0 && <p className="empty-copy">{continuityText.empty}</p>}
                      {continuityViolations.slice(0, 8).map((item: any, index: number) => (
                        <div key={`${item?.code || 'violation'}-${index}`} className="impact-row">
                          <div className="impact-row__title">
                            <span className={`impact-type ${item?.severity === 'error' ? 'impact-change is-warning' : ''}`}>
                              {item?.severity === 'error' ? continuityText.failed : continuityText.passed}
                            </span>
                            <strong>{item?.scene_title || item?.code || 'continuity_issue'}</strong>
                          </div>
                          <div className="impact-note">{item?.message || ''}</div>
                        </div>
                      ))}
                      <CriticNarrativePanel analysis={narrativeAnalysis} locale={locale} />
                      <CausalGraphPanel graph={continuityReport?.graph || null} locale={locale} />
                    </div>
                  )}
                </div>
              </details>
            </section>
          )}
          </section>
        </main>

        <aside className="app-panel app-panel--right">
          <BackendWorkflowPanel mobile={false} fallbackTip={status} docked />
          <XiaohongshuResearchPanel locale={locale} onStatusChange={setStatus} />
          {impactPanel}
          {inspectorPanel}

          <section className="panel-card panel-card--assistant">
            <AIAssistant
              workspacePayload={currentWorkspacePayload}
              selectedId={selectedId}
              selectedSummary={
                selected
                  ? {
                      id: selected.id,
                      type: selected.type,
                      name: selected.name,
                    }
                  : null
              }
              impactAnalysis={impactReport}
              contextSize={assistantContextSize}
              onSuggestion={setAiText}
              locale={locale}
            />
            {aiText && <div className="assistant-suggestion">{aiText}</div>}
          </section>

          {false && <section className="panel-card replay-card">
            <div className="panel-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="library" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{replayText.eyebrow}</div>
                  <h2>{replayText.title}</h2>
                </div>
              </div>
            </div>

            <div className="panel-card__summary replay-card__summary">
              <span>{replayText.summary}</span>
            </div>

            {(authState.recentRuns || []).length === 0 && <p className="empty-copy">{replayText.noRuns}</p>}

            {(authState.recentRuns || []).length > 0 && (
              <>
                <div className="replay-run-list">
                  {(authState.recentRuns || []).map((run: any) => {
                    const runId = String(run.id || '');
                    const statusClass = runStatusTone(run.status);
                    return (
                      <button
                        key={runId}
                        type="button"
                        className={`replay-run-chip ${runId === selectedRunId ? 'is-active' : ''}`}
                        onClick={() => setSelectedRunId(runId)}
                      >
                        <div className="replay-run-chip__head">
                          <strong>{formatRunType(run.generation_type)}</strong>
                          <span className={`replay-run-chip__status ${statusClass}`}>{formatRunStatus(run.status)}</span>
                        </div>
                        <span className="replay-run-chip__meta">{formatReplayTimestamp(run.created_at || run.completed_at)}</span>
                      </button>
                    );
                  })}
                </div>

                {loadingRunRecord && <p className="empty-copy">{replayText.loading}</p>}

                {!loadingRunRecord && replayRecord && (
                  <div className="replay-detail">
                    <div className="replay-hero">
                      <div className="replay-hero__head">
                        <div className="replay-hero__title">
                          <div className="panel-card__eyebrow">
                            {formatReplayTimestamp(replayRecord.created_at || replayRecord.completed_at)}
                          </div>
                          <strong>{formatRunType(replayRecord.generation_type)}</strong>
                        </div>
                        <span className={`app-pill ${replayRiskPillClass}`}>
                          <StudioIcon name="spark" />
                          <span>
                            {replayText.score}:{' '}
                            {replaySimilarity?.available
                              ? `${replaySimilarity?.overall_score || 0} 路 ${replayText.riskLabels[replayRiskLevel] || replayRiskLevel}`
                              : replayText.traceMissing}
                          </span>
                        </span>
                      </div>
                      <div className="replay-stat-grid">
                        <div className="replay-stat-card">
                          <span>{replayText.novelty}</span>
                          <strong>{String(replayTrace?.novelty_level || replayRequest?.novelty_level || 'medium')}</strong>
                        </div>
                        <div className="replay-stat-card">
                          <span>{replayText.referenceMode}</span>
                          <strong>
                            {replayText.modeLabels[String(replayRequest?.reference_mode || 'disabled')] ||
                              String(replayRequest?.reference_mode || 'disabled')}
                          </strong>
                        </div>
                        <div className="replay-stat-card">
                          <span>{replayText.selectedReferences}</span>
                          <strong>{replaySelectedReferences.length}</strong>
                        </div>
                        <div className="replay-stat-card">
                          <span>{replayText.score}</span>
                          <strong>{replaySimilarity?.available ? replaySimilarity?.overall_score || 0 : '--'}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="impact-metrics">
                      <span className="app-pill">
                        <StudioIcon name="structure" />
                        <span>{formatRunType(replayRecord.generation_type)}</span>
                      </span>
                      <span className={`app-pill ${replayRiskPillClass}`}>
                        <StudioIcon name="spark" />
                        <span>
                          {replayText.score}:{' '}
                          {replaySimilarity?.available
                            ? `${replaySimilarity?.overall_score || 0} · ${replayText.riskLabels[replayRiskLevel] || replayRiskLevel}`
                            : replayText.traceMissing}
                        </span>
                      </span>
                    </div>

                    <details className="panel-dropdown panel-dropdown--content" open>
                      <summary className="panel-dropdown__summary">{replayText.requestConfig}</summary>
                      <div className="panel-dropdown__body">
                        <div className="impact-list">
                          <div className="impact-row">
                            <div className="impact-row__title">
                              <span className="impact-type">{replayText.referenceMode}</span>
                              <strong>{replayText.modeLabels[String(replayRequest?.reference_mode || 'disabled')] || String(replayRequest?.reference_mode || 'disabled')}</strong>
                            </div>
                          </div>
                          <div className="impact-row">
                            <div className="impact-row__title">
                              <span className="impact-type">{replayText.novelty}</span>
                              <strong>{String(replayRequest?.novelty_level || replayTrace?.novelty_level || 'medium')}</strong>
                            </div>
                          </div>
                          {Number(replayRequest?.target_episode_count || 0) > 0 && (
                            <div className="impact-row">
                              <div className="impact-row__title">
                                <span className="impact-type">{replayText.targetEpisodes}</span>
                                <strong>{Number(replayRequest?.target_episode_count)}</strong>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </details>

                    <details className="panel-dropdown panel-dropdown--content" open>
                      <summary className="panel-dropdown__summary">{replayText.resultTrace}</summary>
                      <div className="panel-dropdown__body">
                        {!replayTrace || Object.keys(replayTrace).length === 0 ? (
                          <p className="empty-copy">{replayText.noTrace}</p>
                        ) : (
                          <div className="impact-list">
                            <div className="impact-row">
                              <div className="impact-row__title">
                                <span className="impact-type">{replayText.novelty}</span>
                                <strong>{String(replayTrace?.novelty_level || 'medium')}</strong>
                              </div>
                            </div>
                            {replaySelectedReferences.length > 0 && (
                              <div className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{replayText.selectedReferences}</span>
                                  <strong>{replaySelectedReferences.map((item: any) => item?.title_zh || item?.title || item?.id).filter(Boolean).join(' · ')}</strong>
                                </div>
                              </div>
                            )}
                            {replayOriginalityRequirements.length > 0 && (
                              <div className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{replayText.originalityRequirements}</span>
                                </div>
                                <div className="impact-row__reasons">
                                  {replayOriginalityRequirements.map((item: string) => (
                                    <span key={item} className="impact-reason">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {Object.keys(replaySelectedAtoms).length > 0 && (
                              <div className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{replayText.selectedAtoms}</span>
                                </div>
                                <div className="impact-row__reasons">
                                  {Object.entries(replaySelectedAtoms).map(([key, values]) => {
                                    const items = Array.isArray(values) ? values.filter(Boolean).slice(0, 2) : [];
                                    if (items.length === 0) {
                                      return null;
                                    }
                                    return (
                                      <span key={key} className="impact-reason">
                                        {replayText.atomLabels[key] || key}: {items.join(' / ')}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            {replayAntiCopyConstraints.length > 0 && (
                              <div className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{replayText.antiCopy}</span>
                                </div>
                                <div className="impact-row__reasons">
                                  {replayAntiCopyConstraints.slice(0, 6).map((item: string) => (
                                    <span key={item} className="impact-reason">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </details>

                    <details className="panel-dropdown panel-dropdown--content" open>
                      <summary className="panel-dropdown__summary">{replayText.similarity}</summary>
                      <div className="panel-dropdown__body">
                        {!replaySimilarity?.available ? (
                          <p className="empty-copy">{replayText.traceMissing}</p>
                        ) : (
                          <div className="impact-list">
                            <div className="impact-row">
                              <div className="impact-row__title">
                                <span className="impact-type">{replayText.score}</span>
                                <strong>
                                  {replaySimilarity?.overall_score} · {replayText.riskLabels[replayRiskLevel] || replayRiskLevel}
                                </strong>
                              </div>
                            </div>
                            {Object.keys(replayVariationDistance).length > 0 && (
                              <div className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{replayText.variationDistance}</span>
                                </div>
                                <div className="impact-row__reasons">
                                  {Object.entries(replayVariationDistance).map(([key, value]) => (
                                    <span key={key} className="impact-reason">
                                      {replayText.variationLabels[key] || key}: {String(value)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {replayReferenceScores.slice(0, 3).map((item: any) => (
                              <div key={item.reference_id || item.title} className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{item.title || item.reference_id}</span>
                                  <strong>{Math.round(Number(item.overall_similarity || 0) * 100)}%</strong>
                                </div>
                                <div className="impact-row__reasons">
                                  {Object.entries(item.dimensions || {}).map(([key, value]) => (
                                    <span key={`${item.reference_id}-${key}`} className="impact-reason">
                                      {replayText.dimensionLabels[key] || key}: {Math.round(Number(value || 0) * 100)}%
                                    </span>
                                  ))}
                                  {(item.marker_hits || []).map((hit: string) => (
                                    <span key={`${item.reference_id}-${hit}`} className="impact-reason impact-reason--global">
                                      {hit}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {replayMitigation.length > 0 && (
                              <div className="impact-row">
                                <div className="impact-row__title">
                                  <span className="impact-type">{replayText.mitigation}</span>
                                </div>
                                <div className="impact-row__reasons">
                                  {replayMitigation.map((item: string) => (
                                    <span key={item} className="impact-reason">
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </details>
                  </div>
                )}

                {!loadingRunRecord && !replayRecord && <p className="empty-copy">{replayText.empty}</p>}
              </>
            )}
          </section>}

        </aside>
      </div>
      </div>

      {showAdminPanel && authState.user?.is_admin && (
        <div className="modal-backdrop" onClick={() => setShowAdminPanel(false)}>
          <div className="modal-card modal-card--admin" onClick={(event) => event.stopPropagation()}>
            <div className="modal-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="assistant" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{ownerText.adminBadge}</div>
                  <h2>{ownerText.manage}</h2>
                </div>
              </div>
              <button className="modal-close" type="button" onClick={() => setShowAdminPanel(false)}>
                {text.close}
              </button>
            </div>

            <div className="admin-grid">
              <section className="admin-card">
                <label className="form-label" htmlFor="admin-code-prefix">
                  {ownerText.prefix}
                </label>
                <input
                  id="admin-code-prefix"
                  className="app-input"
                  type="text"
                  value={newCodePrefix}
                  onChange={(event) => setNewCodePrefix(event.target.value)}
                />

                <label className="form-label" htmlFor="admin-code-quantity">
                  {ownerText.quantity}
                </label>
                <input
                  id="admin-code-quantity"
                  className="app-input"
                  type="number"
                  min="1"
                  max="200"
                  value={newCodeQuantity}
                  onChange={(event) => setNewCodeQuantity(event.target.value)}
                />

                <label className="form-label" htmlFor="admin-code-max-uses">
                  {ownerText.maxUses}
                </label>
                <input
                  id="admin-code-max-uses"
                  className="app-input"
                  type="number"
                  min="1"
                  max="999"
                  value={newCodeMaxUses}
                  onChange={(event) => setNewCodeMaxUses(event.target.value)}
                />

                <label className="form-label" htmlFor="admin-code-notes">
                  {ownerText.notes}
                </label>
                <textarea
                  id="admin-code-notes"
                  className="app-textarea app-textarea--compact"
                  value={newCodeNotes}
                  onChange={(event) => setNewCodeNotes(event.target.value)}
                  placeholder={ownerText.notesPlaceholder}
                />

                <div className="modal-actions">
                  <button className="app-button app-button--ghost" type="button" onClick={loadAdminCodes}>
                    {ownerText.refresh}
                  </button>
                  <button
                    className="app-button app-button--strong"
                    type="button"
                    onClick={createAdminCodes}
                    disabled={adminBusy}
                  >
                    {ownerText.create}
                  </button>
                </div>
              </section>

              <section className="admin-card admin-card--list">
                {adminError && <div className="form-error">{adminError}</div>}
                {!adminError && adminCodes.length === 0 && !adminBusy && (
                  <div className="empty-copy">{ownerText.empty}</div>
                )}

                <div className="admin-code-list">
                  {adminCodes.map((item) => (
                    <div key={item.code} className="admin-code-row">
                      <div className="admin-code-row__main">
                        <strong>{item.code}</strong>
                        <span>
                          {ownerText.usage}: {item.used_count}/{item.max_uses}
                        </span>
                        {item.notes && <p>{item.notes}</p>}
                      </div>
                      <div className="admin-code-row__side">
                        <span className={`admin-code-status ${Number(item.active) === 1 ? 'is-active' : 'is-disabled'}`}>
                          {Number(item.active) === 1 ? ownerText.active : ownerText.disabled}
                        </span>
                        <button
                          className="app-button app-button--ghost app-button--small"
                          type="button"
                          onClick={() => toggleAdminCode(item.code, Number(item.active) !== 1)}
                          disabled={adminBusy}
                        >
                          {Number(item.active) === 1 ? ownerText.disable : ownerText.enable}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      <WorkspaceStarterModal
        locale={locale}
        open={showStarter}
        busy={busy}
        snapshots={starterSnapshots}
        loadingSnapshots={loadingStarterSnapshots}
        onClose={() => setShowStarter(false)}
        onStartBlank={startBlankDraft}
        onApplyPreset={applyPresetDraft}
        onLoadLatest={() => {
          setShowStarter(false);
          void loadLatest(true);
        }}
        onLoadSavedScript={() => {
          setShowStarter(false);
          void loadScript();
        }}
        onLoadSnapshot={loadSnapshotById}
      />

      <NovelAdaptationModal
        locale={locale}
        open={showNovelAdaptation}
        busy={busy}
        onClose={() => setShowNovelAdaptation(false)}
        onApply={(workspaceSeed, packet) => {
          if ((dirty || elements.length || script) && !window.confirm(locale === 'en' ? 'Replace the current unsaved work with this adaptation?' : '这会用小说改编底稿替换当前未保存内容，继续吗？')) return;
          hydrate(workspaceSeed);
          setPrompt(workspaceSeed?.workspace_meta?.sceneweave?.brief?.requirements || `基于《${packet?.title || '原著'}》继续改编；区分原著事实与改编创造，遵守已确认的保留要求及允许的变更。`);
          setDirty(true);
          setShowNovelAdaptation(false);
          if (workspaceSeed?.script_text) {
            setScript({ script_text: workspaceSeed.script_text, story_text: workspaceSeed.script_text, sceneweave: workspaceSeed.workspace_meta?.sceneweave });
            setShowStructure(false);
            setStatus(locale === 'en' ? 'SceneWeave adaptation loaded. You can edit and save it.' : '织梦改编剧本已写入，可以继续编辑和保存。');
          } else {
            setShowStructure(true);
            setStatus('原著底稿已写入，可以继续编辑。');
          }
        }}
      />

      {showStructure && (
        <div className="modal-backdrop" onClick={() => setShowStructure(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="structure" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{text.structureEyebrow}</div>
                  <h2>{text.generateStructure}</h2>
                </div>
              </div>
              <button className="modal-close" type="button" onClick={() => setShowStructure(false)}>
                {text.close}
              </button>
            </div>

            <label className="form-label" htmlFor="worldview-input">
              {text.worldviewLabel}
            </label>
            <textarea
              id="worldview-input"
              className="app-textarea app-textarea--compact"
              value={worldview}
              maxLength={INPUT_MAX_LENGTH.worldview}
              onChange={(event) => {
                setWorldview(event.target.value);
                setDirty(true);
              }}
              placeholder={text.worldviewPlaceholder}
            />

            <label className="form-label" htmlFor="theme-input">
              {text.themeLabel}
            </label>
            <textarea
              id="theme-input"
              className="app-textarea app-textarea--compact"
              value={themes}
              maxLength={INPUT_MAX_LENGTH.themes}
              onChange={(event) => {
                setThemes(event.target.value);
                setDirty(true);
              }}
              placeholder={text.themePlaceholder}
            />

            <label className="form-label" htmlFor="target-episode-count">
              {targetEpisodeUi.label}
            </label>
            <input
              id="target-episode-count"
              className="app-input"
              type="number"
              min="0"
              max="200"
              value={targetEpisodeCount > 0 ? String(targetEpisodeCount) : ''}
              onChange={(event) => {
                const rawValue = Number(event.target.value || 0);
                setTargetEpisodeCount(Number.isFinite(rawValue) && rawValue > 0 ? Math.min(200, rawValue) : 0);
                setDirty(true);
              }}
              placeholder={targetEpisodeUi.placeholder}
            />
            <div className="left-note">{targetEpisodeUi.hint}</div>

            <label className="form-label" htmlFor="drama-series-mode">
              {locale === 'zh' ? '剧集类型' : 'Series type'}
            </label>
            <select
              id="drama-series-mode"
              className="app-input"
              value={dramaSeriesMode}
              onChange={(event) => {
                setDramaSeriesMode(event.target.value === 'long' ? 'long' : 'short');
                setDirty(true);
              }}
            >
              <option value="short">{locale === 'zh' ? '短剧分支' : 'Short-drama branch'}</option>
              <option value="long">{locale === 'zh' ? '长剧分支' : 'Long-drama branch'}</option>
            </select>

            <ReferenceStrategyPanel
              locale={locale}
              worldview={worldview}
              themes={themeAtoms}
              prompt={prompt}
              presetFacets={presetFacets}
              selectedReferenceIds={selectedReferenceIds}
              onReferenceChange={setSelectedReferenceIds}
              referenceMode={referenceMode}
              onReferenceModeChange={setReferenceMode}
              noveltyLevel={noveltyLevel}
              onNoveltyLevelChange={setNoveltyLevel}
            />

            {!authState?.authenticated && (
              <div className="form-error">
                {locale === 'zh'
                  ? '请先登录，再执行结构生成。'
                  : 'Log in with an invite code before generating structure.'}
              </div>
            )}
            <div className="left-note">{uiText.autoSaveBeforeGenerate}</div>

            <div className="modal-actions">
              <button className="app-button app-button--ghost" type="button" onClick={() => setShowStructure(false)}>
                {text.cancel}
              </button>
              <button className="app-button app-button--strong" type="button" onClick={runStructure} disabled={busy || !authState?.authenticated}>
                <span className="app-button__content">
                  <StudioIcon name="structure" />
                  <span>{busy ? text.processing : text.startGenerate}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showScript && (
        <div className="modal-backdrop" onClick={() => setShowScript(false)}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-card__header">
              <div className="panel-title">
                <span className="panel-title__icon">
                  <StudioIcon name="script" />
                </span>
                <div>
                  <div className="panel-card__eyebrow">{text.writerEyebrow}</div>
                  <h2>{text.generateScript}</h2>
                </div>
              </div>
              <button className="modal-close" type="button" onClick={() => setShowScript(false)}>
                {text.close}
              </button>
            </div>

            <label className="form-label" htmlFor="script-prompt">
              {text.writingInstruction}
            </label>
            <textarea
              id="script-prompt"
              className="app-textarea"
              value={prompt}
              maxLength={INPUT_MAX_LENGTH.prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={text.writingPlaceholder}
            />

            <ReferenceStrategyPanel
              locale={locale}
              worldview={worldview}
              themes={themeAtoms}
              prompt={prompt}
              presetFacets={presetFacets}
              selectedReferenceIds={selectedReferenceIds}
              onReferenceChange={setSelectedReferenceIds}
              referenceMode={referenceMode}
              onReferenceModeChange={setReferenceMode}
              noveltyLevel={noveltyLevel}
              onNoveltyLevelChange={setNoveltyLevel}
            />

            {!authState?.authenticated && (
              <div className="form-error">
                {locale === 'zh'
                  ? '请先登录，再执行剧本生成。'
                  : 'Log in with an invite code before generating script.'}
              </div>
            )}
            <div className="left-note">{uiText.autoSaveBeforeGenerate}</div>

            <div className="script-mode-panel">
              <p className="script-mode-panel__note">{scriptModeText.note}</p>
              {!hasSavedAffectedSceneTargets && (
                <div className="left-note">{scriptModeText.partialRequiresImpact}</div>
              )}
              <div className="script-mode-grid">
                <button
                  className="app-button"
                  type="button"
                  onClick={runPartialScript}
                  disabled={busy || !authState?.authenticated || !hasSavedAffectedSceneTargets}
                >
                  <span className="app-button__content">
                    <StudioIcon name="structure" />
                    <span>{busy ? text.processing : scriptModeText.partialLabel}</span>
                  </span>
                </button>
                <button className="app-button app-button--strong" type="button" onClick={runScript} disabled={busy || !authState?.authenticated}>
                  <span className="app-button__content">
                    <StudioIcon name="quill" />
                    <span>{busy ? text.processing : scriptModeText.fullLabel}</span>
                  </span>
                </button>
                <button className="app-button app-button--ghost" type="button" onClick={runPolishScript} disabled={busy || !authState?.authenticated}>
                  <span className="app-button__content">
                    <StudioIcon name="spark" />
                    <span>{busy ? text.processing : scriptModeText.polishLabel}</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="modal-actions">
              <button className="app-button app-button--ghost" type="button" onClick={() => setShowScript(false)}>
                {text.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
